import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../AuthProvider'
import { colorPersistence } from '../../utils/colorPersistence'
import { 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Search, 
  Plus, 
  ExternalLink, 
  QrCode, 
  RefreshCw, 
  BookOpen, 
  X, 
  Music, 
  Globe, 
  Youtube, 
  FileText, 
  Upload as UploadIcon, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Grid,
  List,
  Copy,
  Calendar,
  BarChart3,
  ShoppingCart,
  ShoppingBag,
  User,
  Megaphone,
  Mail,
  Palette,
  Clock,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Sparkles,
  Book
} from 'lucide-react'
import { QRCodeRenderer } from './QRCodeRenderer'
import { getApiBase } from '../../utils/api'
import { usePlanLimits } from '../../hooks/usePlanLimits'
import { UpgradeModal } from '../UpgradeModal'
import { UnifiedQRForm } from './forms/UnifiedQRForm'
import { BrandingSelector } from './BrandingSelector'
import { ColorSelector } from './ColorSelector'
import { SplitFormLayout } from './forms/shared/SplitFormLayout'
import { 
  WebsitePreview, 
  SpotifyPreview, 
  YoutubePreview
} from './previews'
import { BasePreview } from './previews/BasePreview'
import { UnifiedPreview } from './previews/UnifiedPreview'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import config from '../../config'
import { toast } from 'react-hot-toast'

interface QRLink {
  id: string
  title: string
  slug: string
  target_url: string
  type?: 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'
  created_at: string
  book_id?: string
  campaign_id?: string
  scans?: number
  png?: string
  svg?: string
  redirect?: string
  pdf_url?: string
  metadata?: any
  health_status?: 'healthy' | 'error' | 'unknown' | 'warning'
  health_error?: string
  last_health_check?: string
  author_id?: string
  author_account_slug?: string
  branding_type?: 'beyondpages' | 'custom' | 'none'
  custom_colors?: {
    primaryColor: string
    secondaryColor: string
    textColor: string
  }
}

interface Book {
  id: string
  title: string
  isbn?: string
  status: 'draft' | 'published' | 'archived'
  cover_url?: string
}

interface Campaign {
  id: string
  title: string
  status: 'active' | 'paused' | 'completed' | 'draft'
}

interface EmailCapture {
  id: string
  email: string
  created_at: string
  metadata?: {
    source?: string
  }
}

type SortField = 'title' | 'created_at' | 'scans' | 'type' | 'health_status'
type SortDirection = 'asc' | 'desc'
type ViewMode = 'table' | 'grid'

const API_BASE = getApiBase()
const QR_API_BASE = config.API_BASE

export const Links: React.FC = () => {
  const [links, setLinks] = useState<QRLink[]>([])
  const [books, setBooks] = useState<Book[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [showSprungblattModal, setShowSprungblattModal] = useState(false)
  const [sprungblattFormData, setSprungblattFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  })
  const [sprungblattSubmitting, setSprungblattSubmitting] = useState(false)
  const [selectedLink, setSelectedLink] = useState<QRLink | null>(null)
  const [isApiConnected, setIsApiConnected] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null)
  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [healthCheckLoading, setHealthCheckLoading] = useState(false)
  const [selectedBookId, setSelectedBookId] = useState<string>('')
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('')
  const [createType, setCreateType] = useState<'spotify' | 'website' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'>('spotify')
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [brandingType, setBrandingType] = useState<'beyondpages' | 'custom' | 'none'>(() => {
    // Try global persistence first
    const persistedState = colorPersistence.getState()
    if (persistedState?.brandingType) {
      return persistedState.brandingType
    }
    
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem('links_branding_type')
    if (saved && ['beyondpages', 'custom', 'none'].includes(saved)) {
      colorPersistence.updateBrandingType(saved as 'beyondpages' | 'custom' | 'none')
      return saved as 'beyondpages' | 'custom' | 'none'
    }
    return 'beyondpages'
  })
  const [selectedTeamBrandingId, setSelectedTeamBrandingId] = useState<string | null>(null)
  const [currentAccountSlug, setCurrentAccountSlug] = useState<string>('')
  const [customColors, setCustomColors] = useState(() => {
    console.log('🎨 Links - Initializing customColors state')
    
    // Try global persistence first (survives auth state changes)
    const persistedState = colorPersistence.getState()
    if (persistedState?.customColors) {
      console.log('🎨 Links - Using persisted colors:', persistedState.customColors)
      return persistedState.customColors
    }
    
    // Fallback to sessionStorage
    const saved = sessionStorage.getItem('links_custom_colors')
    console.log('🎨 Links - SessionStorage links_custom_colors:', saved)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (parsed.primaryColor && parsed.secondaryColor && parsed.textColor) {
          console.log('🎨 Links - Using sessionStorage colors:', parsed)
          // Save to persistence manager for future use
          colorPersistence.updateColors(parsed)
          return parsed
        }
      } catch (e) {
        console.log('🎨 Links - Failed to parse sessionStorage colors:', e)
      }
    }
    console.log('🎨 Links - Using default colors')
    return {
      primaryColor: '#3B82F6',
      secondaryColor: '#1F2937',
      textColor: '#FFFFFF'
    }
  })
  const [colorMode, setColorMode] = useState<'gradient' | 'structured'>(() => {
    // Try global persistence first
    const persistedState = colorPersistence.getState()
    if (persistedState?.colorMode) {
      return persistedState.colorMode
    }
    
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem('links_color_mode')
    if (saved && ['gradient', 'structured'].includes(saved)) {
      colorPersistence.updateColorMode(saved as 'gradient' | 'structured')
      return saved as 'gradient' | 'structured'
    }
    return 'gradient'
  })
  const [userBranding, setUserBranding] = useState<any>(null)
  
  // Persist customColors to sessionStorage and global persistence when they change
  useEffect(() => {
    console.log('🎨 Links - Saving customColors:', customColors)
    sessionStorage.setItem('links_custom_colors', JSON.stringify(customColors))
    colorPersistence.updateColors(customColors)
  }, [customColors])
  
  // Persist brandingType to sessionStorage and global persistence when it changes
  useEffect(() => {
    console.log('🎨 Links - Saving brandingType:', brandingType)
    sessionStorage.setItem('links_branding_type', brandingType)
    colorPersistence.updateBrandingType(brandingType)
  }, [brandingType])
  
  // Persist colorMode to sessionStorage and global persistence when it changes
  useEffect(() => {
    console.log('🎨 Links - Saving colorMode:', colorMode)
    sessionStorage.setItem('links_color_mode', colorMode)
    colorPersistence.updateColorMode(colorMode)
  }, [colorMode])
  
  // Wrap setBrandingType to add logging
  const handleBrandingTypeChange = useCallback((type: 'beyondpages' | 'custom' | 'none') => {
    console.log('🎨 Links - handleBrandingTypeChange called with:', type)
    console.log('🎨 Links - Current customColors before branding change:', customColors)
    setBrandingType(type)
  }, [customColors])
  
  // Handle modal close and cleanup
  // Clear wizard data from sessionStorage when wizard is closed or completed
  const clearWizardData = useCallback(() => {
    console.log('🧹 Clearing wizard data from sessionStorage')
    sessionStorage.removeItem('qr_wizard_form_data')
    setWizardFormData({})
  }, [])

  const handleModalClose = useCallback(() => {
    console.log('🎨 Links - Modal closing, clearing all persistence')
    // Clear temporary sessionStorage items when modal closes
    sessionStorage.removeItem('links_custom_colors')
    sessionStorage.removeItem('links_branding_type')
    sessionStorage.removeItem('links_color_mode')
    sessionStorage.removeItem('qr_form_custom_colors')
    sessionStorage.removeItem('qr_form_branding_type')
    sessionStorage.removeItem('qr_form_color_mode')

    // Clear global persistence
    colorPersistence.clearState()

    // Reset states
    setShowModal(false)
    setIsEditMode(false)
    setSelectedLink(null)
    setWizardStep(1)
    clearWizardData()
    setNewlyCreatedLink(null) // Reset newly created link
  }, [clearWizardData])
  const [currentSlugInput, setCurrentSlugInput] = useState<string>('')
  const [simpleFormData, setSimpleFormData] = useState<{title: string, target_url: string}>({
    title: '',
    target_url: ''
  })
  
  // New state for AI generated data
  const [aiGeneratedData, setAiGeneratedData] = useState<any>(null)
  
  // New state for email modal
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailCaptures, setEmailCaptures] = useState<EmailCapture[]>([])
  const [emailsLoading, setEmailsLoading] = useState(false)
  const [selectedEmailLink, setSelectedEmailLink] = useState<QRLink | null>(null)
  
  // New state for table functionality
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedLinks, setSelectedLinks] = useState<Set<string>>(new Set())
  const [createError, setCreateError] = useState<string | null>(null)
  
  // State for QR styling modal
  
  // Wizard step state
  const [wizardStep, setWizardStep] = useState(1)
  const [newlyCreatedLink, setNewlyCreatedLink] = useState<QRLink | null>(null)

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeModalFeature, setUpgradeModalFeature] = useState<string | undefined>()
  // Initialize wizardFormData from sessionStorage to persist across tab switches
  const [wizardFormData, setWizardFormData] = useState<any>(() => {
    const savedData = sessionStorage.getItem('qr_wizard_form_data')
    if (savedData) {
      try {
        console.log('🔄 Restoring wizard data from sessionStorage')
        return JSON.parse(savedData)
      } catch (e) {
        console.error('Failed to parse saved wizard data:', e)
        return {}
      }
    }
    return {}
  })

  // Wrapper function to save wizard data to sessionStorage whenever it changes
  const updateWizardFormData = useCallback((data: any) => {
    console.log('💾 Saving wizard data to sessionStorage:', data)
    setWizardFormData(data)
    // Save to sessionStorage for persistence across tab switches
    try {
      sessionStorage.setItem('qr_wizard_form_data', JSON.stringify(data))
    } catch (e) {
      console.error('Failed to save wizard data to sessionStorage:', e)
    }
  }, [])

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewLink, setPreviewLink] = useState<QRLink | null>(null)

  const { user, session } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const bookFilter = searchParams.get('book')
  const campaignFilter = searchParams.get('campaign')
  const highlightId = searchParams.get('highlight')
  const filterErrors = searchParams.get('filter') === 'errors'
  const { checkQRCodeLimitAndRedirect, getQRUsageInfo, canCreateQRCode, canUploadPDF, checkFeatureAccessAndRedirect, refresh } = usePlanLimits()

  // Check API connection
  const checkApiConnection = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`)
      setIsApiConnected(response.ok)
    } catch {
      setIsApiConnected(false)
    }
  }

  // Load books for selection
  const loadBooks = async () => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/books`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setBooks(data || [])
        
        // If coming from books page with book filter, pre-select that book
        if (bookFilter && data.length > 0) {
          const book = data.find((b: Book) => b.id === bookFilter)
          if (book) {
            setSelectedBookId(bookFilter)
          }
        }
      }
    } catch (err) {
      console.error('Failed to load books:', err)
    }
  }

  // Load campaigns for selection
  const loadCampaigns = async () => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/campaigns/for-assignment`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCampaigns(data || [])
        
        // If coming from campaigns page with campaign filter, pre-select that campaign
        if (campaignFilter && data.length > 0) {
          const campaign = data.find((c: Campaign) => c.id === campaignFilter)
          if (campaign) {
            setSelectedCampaignId(campaignFilter)
          }
        }
      }
    } catch (err) {
      console.error('Failed to load campaigns:', err)
    }
  }

  // Load custom colors from link when editing
  useEffect(() => {
    if (isEditMode && selectedLink && selectedLink.custom_colors) {
      setCustomColors(selectedLink.custom_colors)
      handleBrandingTypeChange(selectedLink.branding_type || 'beyondpages')
    }
  }, [isEditMode, selectedLink])

  // Load links from API
  const loadLinks = async () => {
    console.log('🔍 Auth Debug - User:', user ? 'logged in' : 'not logged in')
    console.log('🔍 Auth Debug - Session:', session ? 'exists' : 'missing')
    console.log('🔍 Auth Debug - Access Token:', session?.access_token ? 'available' : 'missing')
    console.log('🔍 API Base URL:', API_BASE)
    console.log('🔍 Window location:', window.location.hostname)
    
    if (!session?.access_token) {
      console.warn('⚠️ No access token available')
      setLoading(false)
      return
    }

    try {
      setError(null)
      console.log('🔍 Loading links from backend...')
      console.log('🔍 Full API URL:', `${API_BASE}/api/links`)
      
      const response = await fetch(`${API_BASE}/api/links`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('🔍 Response status:', response.status)
      console.log('🔍 Response headers:', {
        'content-type': response.headers.get('content-type'),
        'x-error': response.headers.get('x-error')
      })

      if (!response.ok) {
        if (response.status === 401) {
          // Try to get more details about the auth error
          let errorDetails = 'Authentifizierung fehlgeschlagen.'
          try {
            const errorData = await response.json()
            console.error('🔍 Auth error details:', errorData)
            errorDetails = errorData.error || errorDetails
          } catch (e) {
            console.error('🔍 Could not parse error response')
          }
          throw new Error(`${errorDetails} Bitte melden Sie sich erneut an.`)
        }
        throw new Error(`Backend-Fehler: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      console.log('✅ Backend response:', data)
      console.log('📊 Scan counts from backend:', data.map((link: any) => ({
        slug: link.slug,
        scans: link.scans,
        id: link.id
      })))
      
      // Ensure data is an array
      let linksArray = Array.isArray(data) ? data : []
      
      // Filter by book if bookFilter is present
      if (bookFilter) {
        linksArray = linksArray.filter((link: any) => link.book_id === bookFilter)
      }
      
      // Filter by campaign if campaignFilter is present
      if (campaignFilter) {
        linksArray = linksArray.filter((link: any) => link.campaign_id === campaignFilter)
      }
      
      // Use production domain for QR codes, even in development
      const qrDomain = config.QR_DOMAIN
      
      setLinks(linksArray.map((link: any) => {
        // Use new format if account slug is available, otherwise fall back to old format
        const listenUrl = link.author_account_slug 
          ? `${qrDomain}/qr/${link.author_account_slug}/${link.slug}`
          : `${qrDomain}/r/${link.slug}`;
        // Always use full API URL for QR code images
        // Add cache busting parameter - always use timestamp to ensure fresh images
        const timestamp = Date.now();
        const cacheBuster = link.metadata?.styleConfig 
          ? `&t=${timestamp}&styled=1` 
          : `&t=${timestamp}`;
        const qrApiUrl = `${QR_API_BASE}/qr/${link.slug}?format=png${cacheBuster}`;
        
        return {
          ...link,
          scans: link.scans || 0,
          png: qrApiUrl,
          svg: `${QR_API_BASE}/qr/${link.slug}?format=svg${cacheBuster}`,
          redirect: listenUrl // Points to production domain
        }
      }))
      
      // Save to localStorage for the Listen page
      localStorage.setItem('qr_links', JSON.stringify(linksArray));
      
      setError(null)
      console.log(`✅ Loaded ${linksArray.length} links from backend`)
    } catch (err) {
      console.error('❌ Failed to load links:', err)
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der QR-Codes')
      setLinks([]) // Clear any existing data on error
    } finally {
      setLoading(false)
    }
  }

  // Create new link
  const createLink = async (formData: FormData) => {
    if (!session?.access_token) return

    setCreateLoading(true)
    try {
      const title = formData.get('title') as string
      const description = formData.get('description') as string
      const target_url = formData.get('target_url') as string
      const slug = formData.get('slug') as string
      const book_id = formData.get('book_id') as string || null
      const campaign_id = formData.get('campaign_id') as string || null
      const type = formData.get('type') as 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'
      const metadata = formData.get('metadata') as string
      const custom_colors = formData.get('custom_colors') as string
      const pdf_url = formData.get('pdf_url') as string || null

      console.log('Raw metadata from FormData:', metadata)
      
      // Ensure only one of book_id or campaign_id is set
      const linkData: any = { 
        title,
        description, 
        target_url, 
        slug, 
        type, 
        branding_type: brandingType 
      }
      
      // Custom colors will be added to metadata, not as separate field
      
      // Only add book_id if it's not empty
      if (book_id && book_id !== '') {
        linkData.book_id = book_id
      }
      
      // Only add campaign_id if it's not empty AND book_id is not set
      if (campaign_id && campaign_id !== '' && !linkData.book_id) {
        linkData.campaign_id = campaign_id
      }
      
      // Add metadata for new types
      if (metadata) {
        linkData.metadata = metadata
      }
      
      // Add custom colors to the request (backend will merge into metadata)
      if (custom_colors) {
        linkData.custom_colors = custom_colors
      }
      
      // Add pdf_url for PDF type
      if (type === 'pdf' && pdf_url) {
        linkData.pdf_url = pdf_url
        linkData.target_url = pdf_url // For PDF, target_url is the PDF URL
      }

      console.log('🎨 BRANDING DEBUG - Creating link with branding_type:', brandingType)
      console.log('🎨 BRANDING DEBUG - Full linkData:', linkData)
      console.log('📝 Description from formData:', description)
      console.log('📝 Final linkData being sent:', JSON.stringify(linkData, null, 2))

      const response = await fetch(`${API_BASE}/api/links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(linkData)
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Create link error response:', error)
        
        // Provide more helpful error messages
        let errorMessage = error.error || 'Fehler beim Erstellen des QR-Codes'
        if (errorMessage.includes('Slug existiert bereits')) {
          errorMessage = 'Dieser Slug wird bereits verwendet. Bitte wählen Sie einen anderen eindeutigen Namen für Ihre URL.'
        } else if (errorMessage.includes('Invalid sample metadata')) {
          errorMessage = 'Fehler bei der Leseprobe: Bitte stellen Sie sicher, dass alle Pflichtfelder (Titel, Autor, PDF) ausgefüllt sind.'
        } else if (errorMessage.toLowerCase().includes('invalid author metadata') || errorMessage.toLowerCase().includes('missing name')) {
          errorMessage = 'Fehler beim Autorenprofil: Bitte geben Sie einen Namen ein.'
        } else if (errorMessage.includes('missing required fields')) {
          errorMessage = 'Bitte füllen Sie alle Pflichtfelder aus bevor Sie fortfahren.'
        }
        
        throw new Error(errorMessage)
      }

      const newLink = await response.json()
      console.log('✅ Created link:', newLink)
      console.log('🎨 BRANDING DEBUG - Created link branding_type:', newLink.branding_type)
      if (newLink?.metadata?.iframe_compatible === false) {
        toast((t) => (
          <div>
            <div className="font-medium">Direkt öffnen aktiviert</div>
            <div className="text-sm opacity-90">Diese Website erlaubt keine Einbettung. Der QR-Code öffnet die Seite jetzt direkt.</div>
          </div>
        ))
      }
      
      // Add generated URLs
      // Use production domain for QR codes, even in development
      const qrDomain = config.QR_DOMAIN
      // Use new format if account slug is available, otherwise fall back to old format
      const listenUrl = newLink.author_account_slug 
        ? `${qrDomain}/qr/${newLink.author_account_slug}/${newLink.slug}`
        : `${qrDomain}/r/${newLink.slug}`;
      // Always use full API URL for QR code images
      // Add cache busting parameter if link has style config
      const cacheBuster = newLink.metadata?.styleConfig ? `&t=${Date.now()}` : '';
      const qrApiUrl = `${QR_API_BASE}/qr/${newLink.slug}?format=png${cacheBuster}`;
      
      const linkWithUrls = {
        ...newLink,
        scans: 0,
        png: qrApiUrl,
        svg: `${QR_API_BASE}/qr/${newLink.slug}?format=svg${cacheBuster}`,
        redirect: listenUrl
      }

      setLinks(prev => [linkWithUrls, ...prev])

      // Don't close modal immediately - show success step with download button
      setNewlyCreatedLink(linkWithUrls)
      setWizardStep(4) // Move to success step

      // Clear wizard data after successful creation
      clearWizardData()

      setPdfUrl('') // Reset PDF URL
      setAiGeneratedData(null) // Reset AI data
      setCurrentSlugInput('') // Reset slug input
    } catch (err) {
      console.error('❌ Failed to create link:', err)
      // Don't close the modal on error - let user fix the issue
      setCreateLoading(false)

      // Return the error to the form handler
      throw err
    } finally {
      setCreateLoading(false)
    }
  }

  // Update link
  const updateLink = async (formData: FormData) => {
    if (!session?.access_token || !selectedLink) return

    setUpdateLoading(true)
    try {
      const title = formData.get('title') as string
      const description = formData.get('description') as string
      const target_url = formData.get('target_url') as string
      const slug = formData.get('slug') as string
      const book_id = formData.get('book_id') as string || null
      const campaign_id = formData.get('campaign_id') as string || null
      const type = formData.get('type') as 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'
      const metadata = formData.get('metadata') as string
      const custom_colors = formData.get('custom_colors') as string

      const linkData: any = { title, description, target_url, slug, book_id, campaign_id, type, branding_type: brandingType }
      
      // Custom colors will be handled by backend to merge into metadata
      
      // Add metadata for new types
      if (metadata) {
        linkData.metadata = metadata
      }
      
      // Add custom colors to the request (backend will merge into metadata)
      if (custom_colors) {
        linkData.custom_colors = custom_colors
      }
      
      // Add pdf_url for PDF type
      const pdf_url = formData.get('pdf_url') as string || null
      if (type === 'pdf' && pdf_url) {
        linkData.pdf_url = pdf_url
        linkData.target_url = pdf_url // For PDF, target_url is the PDF URL
      }

      console.log('🎨 BRANDING DEBUG - Updating link with branding_type:', brandingType)
      console.log('📝 Updating link:', selectedLink.id, linkData)

      const response = await fetch(`${API_BASE}/api/links/${selectedLink.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(linkData)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Fehler beim Aktualisieren des QR-Codes')
      }

      const updatedLink = await response.json()
      console.log('✅ Updated link:', updatedLink)
      console.log('🎨 BRANDING DEBUG - Updated link branding_type:', updatedLink.branding_type)
      if (updatedLink?.metadata?.iframe_compatible === false) {
        toast((t) => (
          <div>
            <div className="font-medium">Direkt öffnen aktiviert</div>
            <div className="text-sm opacity-90">Diese Website erlaubt keine Einbettung. Der QR-Code öffnet die Seite jetzt direkt.</div>
          </div>
        ))
      }
      
      // Use production domain for QR codes, even in development
      const qrDomain = config.QR_DOMAIN
      // Use new format if account slug is available, otherwise fall back to old format
      const listenUrl = updatedLink.author_account_slug 
        ? `${qrDomain}/qr/${updatedLink.author_account_slug}/${updatedLink.slug}`
        : `${qrDomain}/r/${updatedLink.slug}`;
      // Always use full API URL for QR code images
      // Add cache busting parameter if link has style config
      const cacheBuster = updatedLink.metadata?.styleConfig ? `&t=${Date.now()}` : '';
      const qrApiUrl = `${QR_API_BASE}/qr/${updatedLink.slug}?format=png${cacheBuster}`;
      
      setLinks(prev => prev.map(link => 
        link.id === selectedLink.id ? { 
          ...updatedLink, 
          scans: link.scans,
          png: qrApiUrl,
          svg: `${QR_API_BASE}/qr/${updatedLink.slug}?format=svg${cacheBuster}`,
          redirect: listenUrl
        } : link
      ))
      handleModalClose()
      setPdfUrl('') // Reset PDF URL
    } catch (err) {
      console.error('❌ Failed to update link:', err)
      alert(err instanceof Error ? err.message : 'Fehler beim Aktualisieren des QR-Codes')
    } finally {
      setUpdateLoading(false)
    }
  }

  // Helper: sync usage on server and refresh local subscription
  const syncUsage = useCallback(async () => {
    try {
      if (!session?.access_token) return
      await fetch(`${API_BASE}/api/billing/subscription/sync-usage`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })
      await refresh()
    } catch (e) {
      // non-blocking
      console.warn('Usage sync after delete failed', e)
    }
  }, [refresh, session])

  // Delete link with confirmation and usage refresh
  const deleteLink = async (id: string) => {
    if (!window.confirm('QR-Code wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return
    }
    if (!session?.access_token) return

    setDeleteLoading(id)
    try {
      console.log('🗑️ Deleting link:', id)

      const response = await fetch(`${API_BASE}/api/links/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Fehler beim Löschen des QR-Codes')
      }

      // Remove from local state immediately
      setLinks(prev => prev.filter(link => link.id !== id))
      // Sync usage and refresh plan counters
      await syncUsage()
      toast.success('QR-Code gelöscht')
      console.log('✅ Successfully deleted link:', id)
    } catch (err) {
      console.error('❌ Failed to delete link:', err)
      alert(err instanceof Error ? err.message : 'Fehler beim Löschen des QR-Codes')
    } finally {
      setDeleteLoading(null)
    }
  }

  // Download QR code function
  const downloadQRCode = async (url: string, filename: string) => {
    try {
      console.log('📥 Downloading QR code:', url)
      const response = await fetch(url)
      
      console.log('📊 Response status:', response.status)
      console.log('📊 Response headers:', {
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length'),
        contentDisposition: response.headers.get('content-disposition')
      })
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }
      
      const blob = await response.blob()
      console.log('📊 Blob info:', {
        size: blob.size,
        type: blob.type
      })
      
      // Check if we got HTML instead of an image
      if (blob.type.includes('text/html') || blob.size < 100) {
        console.error('❌ Received HTML/text instead of image!')
        // Try to read the content
        const text = await blob.text()
        console.error('Content received:', text.substring(0, 200))
        alert('Fehler: Server gibt kein Bild zurück. Bitte prüfen Sie die Konsole.')
        return
      }
      
      const objectUrl = URL.createObjectURL(blob)
      console.log('📊 Object URL created:', objectUrl)
      
      // Create temporary link and trigger download
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100)
      console.log('✅ Download complete:', filename)
    } catch (error) {
      console.error('❌ Download error:', error)
      alert('Fehler beim Herunterladen des QR-Codes. Details in der Konsole.')
    }
  }

  // Filter links by search term
  const filteredLinks = links.filter(link => {
    // First apply error filter if active
    if (filterErrors && link.health_status !== 'error') {
      return false
    }
    
    // Then apply search filter
    return link.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.target_url.toLowerCase().includes(searchTerm.toLowerCase())
  })

  // Sort links
  const sortedLinks = [...filteredLinks].sort((a, b) => {
    let aValue: any, bValue: any
    
    switch (sortField) {
      case 'title':
        aValue = a.title.toLowerCase()
        bValue = b.title.toLowerCase()
        break
      case 'created_at':
        aValue = new Date(a.created_at).getTime()
        bValue = new Date(b.created_at).getTime()
        break
      case 'scans':
        aValue = a.scans || 0
        bValue = b.scans || 0
        break
      case 'type':
        aValue = a.type || 'website'
        bValue = b.type || 'website'
        break
      case 'health_status':
        aValue = a.health_status || 'unknown'
        bValue = b.health_status || 'unknown'
        break
      default:
        return 0
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Handle bulk selection
  const handleSelectAll = () => {
    if (selectedLinks.size === sortedLinks.length) {
      setSelectedLinks(new Set())
    } else {
      setSelectedLinks(new Set(sortedLinks.map(link => link.id)))
    }
  }

  const handleSelectLink = (linkId: string) => {
    const newSelected = new Set(selectedLinks)
    if (newSelected.has(linkId)) {
      newSelected.delete(linkId)
    } else {
      newSelected.add(linkId)
    }
    setSelectedLinks(newSelected)
  }

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedLinks.size === 0) return
    if (!window.confirm(`Ausgewählte ${selectedLinks.size} QR-Codes löschen?`)) return
    for (const linkId of selectedLinks) {
      await deleteLink(linkId)
    }
    setSelectedLinks(new Set())
  }

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    })
  }

  // Get book title by ID
  const getBookTitle = (bookId?: string) => {
    if (!bookId) return null
    const book = books.find(b => b.id === bookId)
    return book?.title || null
  }

  // Get campaign title by ID
  const getCampaignTitle = (campaignId?: string) => {
    if (!campaignId) return null
    const campaign = campaigns.find(c => c.id === campaignId)
    return campaign?.title || null
  }

  // Load current account slug
  const loadAccountSlug = async () => {
    if (!user?.id) return

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('account_slug')
        .eq('id', user.id)
        .single()

      if (data?.account_slug) {
        setCurrentAccountSlug(data.account_slug)
      }
    } catch (error) {
      console.error('Failed to load account slug:', error)
    }
  }

  // Load user branding when brandingType changes to custom
  const loadUserBranding = async () => {
    if (!session?.access_token || brandingType !== 'custom') return

    try {
      const response = await fetch(`${API_BASE}/api/branding`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUserBranding(data)
        if (data) {
          setCustomColors({
            primaryColor: data.primary_color || '#3B82F6',
            secondaryColor: data.secondary_color || '#1F2937',
            textColor: data.text_color || '#FFFFFF'
          })
        }
      }
    } catch (error) {
      console.error('Failed to load user branding:', error)
    }
  }

  useEffect(() => {
    checkApiConnection()
    loadBooks()
    loadCampaigns()
    loadLinks()
    loadAccountSlug()
  }, [session, bookFilter, campaignFilter, user])
  
  // Auto-refresh scan statistics every 30 seconds
  useEffect(() => {
    if (!session) return
    
    const interval = setInterval(() => {
      if (!loading) {
        console.log('🔄 Auto-refreshing scan statistics...')
        loadLinks()
      }
    }, 30000)
    
    return () => clearInterval(interval)
  }, [session, loading])

  useEffect(() => {
    loadUserBranding()
  }, [brandingType, session])

  // Redirect to login if no user - moved to useEffect to prevent render warnings
  useEffect(() => {
    if (!user) {
      navigate('/qr/login')
    }
  }, [user, navigate])

  const handleCreateQRCode = () => {
    // Check plan limits before opening modal
    if (!canCreateQRCode()) {
      // Show upgrade modal instead of redirecting
      const usage = getQRUsageInfo()
      setUpgradeModalFeature('QR-Code erstellen')
      setShowUpgradeModal(true)
      return
    }
    
    setIsEditMode(false)
    setSelectedLink(null)

    // Create a new draft
    // Reset wizard state
    setWizardStep(1)
    clearWizardData()
    setNewlyCreatedLink(null) // Reset newly created link

    setShowModal(true)
    // Reset to default type
    setCreateType('spotify')
  }

  // Get usage info for visual indicators
  const qrUsageInfo = getQRUsageInfo()
  const isQRLimitReached = !canCreateQRCode()

  // Get icon for link type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'spotify':
        return <Music className="w-4 h-4 text-green-600" />
      case 'youtube':
        return <Youtube className="w-4 h-4 text-red-600" />
      case 'pdf':
        return <FileText className="w-4 h-4 text-blue-600" />
      case 'multishop':
        return <ShoppingCart className="w-4 h-4 text-purple-600" />
      case 'author':
        return <User className="w-4 h-4 text-indigo-600" />
      case 'sample':
        return <BookOpen className="w-4 h-4 text-amber-600" />
      case 'website':
      default:
        return <Globe className="w-4 h-4 text-gray-500" />
    }
  }

  // Type selection component
  const TypeSelectionComponent = () => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Typ *
      </label>
      <div className="grid grid-cols-2 gap-3">
        {/* Simple types in first column */}
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="spotify"
              checked={createType === 'spotify'}
              onChange={(e) => setCreateType('spotify' as const)}
              className="mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Spotify Playlist</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="website"
              checked={createType === 'website'}
              onChange={(e) => setCreateType('website' as const)}
              className="mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Website</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="youtube"
              checked={createType === 'youtube'}
              onChange={(e) => setCreateType('youtube' as const)}
              className="mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">YouTube Video</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="pdf"
              checked={createType === 'pdf'}
              onChange={(e) => {
                if (!canUploadPDF()) {
                  alert('PDF-Upload ist nur in kostenpflichtigen Plänen verfügbar. Bitte upgraden Sie Ihren Plan.');
                  return;
                }
                setCreateType('pdf' as const);
              }}
              className="mr-2 focus:ring-blue-500"
            />
            <span className={`text-sm ${canUploadPDF() ? 'text-gray-700' : 'text-gray-400'}`}>
              PDF Dokument {!canUploadPDF() && '(Premium)'}
            </span>
          </label>
        </div>
        {/* Complex types in second column */}
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="multishop"
              checked={createType === 'multishop'}
              onChange={(e) => setCreateType('multishop' as const)}
              className="mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Multi-Shop (Buchhandel)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="author"
              checked={createType === 'author'}
              onChange={(e) => setCreateType('author' as const)}
              className="mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Autorenprofil</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="type"
              value="sample"
              checked={createType === 'sample'}
              onChange={(e) => setCreateType('sample' as const)}
              className="mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Leseprobe</span>
          </label>
        </div>
      </div>
    </div>
  )

  // Handle PDF upload
  const handlePdfUpload = async (file: File) => {
    if (!session?.access_token) return

    setUploadingPdf(true)
    try {
      const formData = new FormData()
      formData.append('pdf', file)

      const response = await fetch(`${API_BASE}/api/upload/pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Fehler beim Hochladen der PDF')
      }

      const { url } = await response.json()
      setPdfUrl(url)
      console.log('✅ PDF uploaded:', url)
    } catch (err) {
      console.error('❌ Failed to upload PDF:', err)
      alert(err instanceof Error ? err.message : 'Fehler beim Hochladen der PDF')
    } finally {
      setUploadingPdf(false)
    }
  }

  const generateQRCodeUrl = (slug: string, format: 'png' | 'svg' = 'png', size: number = 200) => {
    const qrDomain = config.QR_DOMAIN
    return `${qrDomain}/api/links/qr/${slug}?format=${format}&size=${size}`
  }

  const handleCopyLink = (link: QRLink) => {
    const qrDomain = config.QR_DOMAIN
    // Use new format if account slug is available, otherwise fall back to old format
    const fullUrl = link.author_account_slug 
      ? `${qrDomain}/qr/${link.author_account_slug}/${link.slug}`
      : `${qrDomain}/r/${link.slug}`
    copyToClipboard(fullUrl)
  }

  const handleTestLink = (link: QRLink) => {
    // Show preview modal instead of opening new window
    setPreviewLink(link)
    setShowPreviewModal(true)
  }

  // Check for navigation state from AI Ideas and query parameters
  useEffect(() => {
    // Check for query parameters first
    const createParam = searchParams.get('create')
    const editParam = searchParams.get('edit')
    const focusParam = searchParams.get('focus')
    
    if (createParam === 'true') {
      console.log('📝 Opening create modal from query params')
      setIsEditMode(false)
      setSelectedLink(null)
      setShowModal(true)
      
      // Clear the query params after opening modal
      navigate(location.pathname, { replace: true })
      
      // Focus on first input after modal opens
      if (focusParam === 'true') {
        setTimeout(() => {
          const firstInput = document.querySelector('#create-modal input:not([type="radio"])') as HTMLInputElement
          if (firstInput) {
            firstInput.focus()
          }
        }, 100)
      }
    } else if (editParam && links.length > 0) {
      // Find and edit the specified link
      const linkToEdit = links.find(l => l.id === editParam)
      if (linkToEdit) {
        console.log('📝 Opening edit modal for link:', editParam)
        setSelectedLink(linkToEdit)
        setIsEditMode(true)
        setShowModal(true)
        // Set create type based on selected link type
        setCreateType(linkToEdit.type as any || 'website')
        handleBrandingTypeChange(linkToEdit.branding_type || 'beyondpages')
        // Set custom colors if available (check both locations)
        if (linkToEdit.custom_colors) {
          setCustomColors(linkToEdit.custom_colors)
        } else if (linkToEdit.metadata?.customColors) {
          setCustomColors(linkToEdit.metadata.customColors)
        }
        // Set color mode if available
        if (linkToEdit.metadata?.colorMode) {
          setColorMode(linkToEdit.metadata.colorMode)
        }
        
        // Clear the query params after opening modal
        navigate(location.pathname, { replace: true })
        
        // Focus on first input after modal opens
        if (focusParam === 'true') {
          setTimeout(() => {
            const firstInput = document.querySelector('#edit-modal input:not([type="radio"])') as HTMLInputElement
            if (firstInput) {
              firstInput.focus()
            }
          }, 100)
        }
      }
    } else if (location.state && location.state.createNew) {
      const state = location.state as any
      console.log('🤖 Received AI Ideas state:', state)
      
      // Set the create type based on the AI suggestion
      if (state.type) {
        setCreateType(state.type)
      }
      
      // Store AI generated data
      if (state.qrData || state.aiGeneratedContent) {
        setAiGeneratedData({
          title: state.title,
          qrData: state.qrData,
          aiContent: state.aiGeneratedContent
        })
      }
      
      // Pre-fill book if provided
      if (state.bookTitle) {
        // Try to find the book by title
        const matchingBook = books.find(b => b.title === state.bookTitle)
        if (matchingBook) {
          setSelectedBookId(matchingBook.id)
        }
      }
      
      // Open the create modal
      setIsEditMode(false)
      setSelectedLink(null)
      setShowModal(true)
      
      // Clear the navigation state to prevent re-opening on refresh
      navigate(location.pathname, { replace: true })
    }
  }, [location.state, books, navigate, location.pathname, searchParams, links])

  // Update form data when entering edit mode
  useEffect(() => {
    if (isEditMode && selectedLink) {
      setSimpleFormData({
        title: selectedLink.title || '',
        target_url: selectedLink.target_url || ''
      })
      setPdfUrl(selectedLink.pdf_url || '')
      
      // Load custom colors from metadata if available
      if (selectedLink.metadata?.customColors) {
        setCustomColors(selectedLink.metadata.customColors)
      } else if (selectedLink.custom_colors) {
        // Fallback to root-level custom_colors
        setCustomColors(selectedLink.custom_colors)
      }
      
      // Load color mode from metadata if available
      if (selectedLink.metadata?.colorMode) {
        setColorMode(selectedLink.metadata.colorMode)
      }
    }
  }, [isEditMode, selectedLink])

  // Load captured emails for a link
  const loadCapturedEmails = async (link: QRLink) => {
    if (!session?.access_token) return
    
    setEmailsLoading(true)
    setSelectedEmailLink(link)
    setShowEmailModal(true)
    
    try {
      const response = await fetch(`${API_BASE}/api/links/${link.id}/emails`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setEmailCaptures(data.emails || [])
      } else {
        console.error('Failed to load emails')
        setEmailCaptures([])
      }
    } catch (error) {
      console.error('Error loading emails:', error)
      setEmailCaptures([])
    } finally {
      setEmailsLoading(false)
    }
  }

  // Export all captured emails
  const exportEmails = async (format: 'csv' | 'json' = 'csv') => {
    if (!session?.access_token) return
    
    try {
      const response = await fetch(`${API_BASE}/api/links/emails/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        if (format === 'csv') {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'captured-emails.csv'
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        } else {
          const data = await response.json()
          console.log('Exported emails:', data)
        }
      }
    } catch (error) {
      console.error('Error exporting emails:', error)
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">QR-Codes verwalten</h1>
          <p className="text-gray-600 mt-1">
            {bookFilter && books.find(b => b.id === bookFilter) ? (
              <>Für Buch: <span className="font-medium">{books.find(b => b.id === bookFilter)?.title}</span> • </>
            ) : null}
            {campaignFilter && campaigns.find(c => c.id === campaignFilter) ? (
              <>Für Kampagnen: <span className="font-medium">{campaigns.find(c => c.id === campaignFilter)?.title}</span> • </>
            ) : null}
            {filterErrors ? (
              <span className="text-red-600 font-medium">Zeige nur fehlerhafte QR-Codes</span>
            ) : (
              isApiConnected ? (
                `${links.length} QR-Code${links.length !== 1 ? 's' : ''} geladen`
              ) : (
                'Backend-Verbindung prüfen...'
              )
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {(bookFilter || campaignFilter || filterErrors) && (
            <button
              onClick={() => navigate('/qr/dash/links')}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Filter zurücksetzen
            </button>
          )}
          <button
            onClick={handleCreateQRCode}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
              isQRLimitReached 
                ? 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 cursor-pointer' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={isQRLimitReached ? `Plan-Limit erreicht (${qrUsageInfo?.used}/${qrUsageInfo?.limit})` : undefined}
            data-testid="links-create"
          >
            <Plus className={`w-4 h-4 ${isQRLimitReached ? 'opacity-50' : ''}`} />
            {isQRLimitReached ? 'Neuer QR-Code (Limit erreicht)' : 'Neuer QR-Code'}
          </button>
          <button
            onClick={() => setShowSprungblattModal(true)}
            className="px-4 py-2 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center gap-2 transition-colors"
            title="Individuelle Web-Apps für Ihre QR-Codes entwickeln lassen"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3L21 21M21 3L3 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
            <span className="hidden sm:inline">Erweiterte Apps</span>
            <ExternalLink className="w-3 h-3 sm:hidden" />
          </button>
        </div>
      </div>

      {/* Backend Connection Status */}
      {!isApiConnected && (
        <div className="mb-6 p-4 bg-yellow-100 border border-yellow-300 rounded-lg text-yellow-800">
          ⚠️ Backend nicht erreichbar. Stellen Sie sicher, dass der Backend-Server läuft (Port 3002).
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="QR-Codes durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="links-search-input"
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
          </div>

          {/* Results Count */}
          <span className="text-sm text-gray-600">
            {sortedLinks.length} von {links.length} QR-Code{sortedLinks.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Bulk Actions */}
        {selectedLinks.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedLinks.size} ausgewählt
            </span>
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 text-white px-3 py-1 rounded-md text-sm hover:bg-red-700 flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </button>
            <button
              onClick={() => setSelectedLinks(new Set())}
              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-sm hover:bg-gray-200"
            >
              Abbrechen
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg text-red-700">
          ❌ {error}
          <button 
            onClick={loadLinks}
            className="ml-4 text-sm underline hover:no-underline"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">QR-Codes werden vom Backend geladen...</p>
        </div>
      )}

      {/* Plan Usage Indicator */}
      {!loading && qrUsageInfo && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">QR-Code Nutzung</h3>
              <p className="text-sm text-gray-600 mt-1">
                {qrUsageInfo.used} von {qrUsageInfo.limit} QR-Codes verwendet
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Progress Bar */}
              <div className="w-32">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      qrUsageInfo.used >= qrUsageInfo.limit 
                        ? 'bg-red-500' 
                        : qrUsageInfo.used / qrUsageInfo.limit > 0.8 
                        ? 'bg-yellow-500' 
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((qrUsageInfo.used / qrUsageInfo.limit) * 100, 100)}%` }}
                  />
                </div>
              </div>
              {qrUsageInfo.used >= qrUsageInfo.limit && (
                <button
                  onClick={() => {
                    setUpgradeModalFeature('Mehr QR-Codes erstellen')
                    setShowUpgradeModal(true)
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Upgrade für mehr
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error and Warning Alerts */}
      {!loading && links.filter(l => l.health_status === 'error').length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800">
              {links.filter(l => l.health_status === 'error').length} fehlerhafte{' '}
              {links.filter(l => l.health_status === 'error').length === 1 ? 'QR-Code' : 'QR-Codes'} gefunden
            </h3>
            <p className="text-sm text-red-700 mt-1">
              Diese QR-Codes funktionieren möglicherweise nicht korrekt. Bitte überprüfen Sie die markierten Einträge.
            </p>
            {filterErrors ? (
              <button
                onClick={() => navigate('/qr/dash/links')}
                className="text-sm text-red-600 hover:text-red-700 underline mt-2"
              >
                Alle QR-Codes anzeigen
              </button>
            ) : (
              <button
                onClick={() => navigate('/qr/dash/links?filter=errors')}
                className="text-sm text-red-600 hover:text-red-700 underline mt-2"
              >
                Nur fehlerhafte anzeigen
              </button>
            )}
          </div>
        </div>
      )}
      
      {!loading && links.filter(l => l.health_status === 'warning').length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              {links.filter(l => l.health_status === 'warning').length} QR-Code{' '}
              {links.filter(l => l.health_status === 'warning').length === 1 ? 'hat' : 'haben'} Einbettungsprobleme
            </h3>
            <p className="text-sm text-yellow-700 mt-1">
              Diese Websites können nicht in iframe eingebettet werden (z.B. web.de, gmx.de). 
              Die QR-Codes funktionieren, aber die Vorschau ist möglicherweise eingeschränkt.
            </p>
          </div>
        </div>
      )}

      {/* Links Content */}
      {!loading && (
        <>
          {viewMode === 'table' ? (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent bg-gray-50">
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedLinks.size === sortedLinks.length && sortedLinks.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </TableHead>
                    <TableHead className="w-16 text-xs uppercase tracking-wider">
                      QR
                    </TableHead>
                    <TableHead className="w-48 text-xs uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('title')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Titel
                        {sortField === 'title' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-24 text-xs uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('type')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Typ
                        {sortField === 'type' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-40 text-xs uppercase tracking-wider">
                      Ziel-URL
                    </TableHead>
                    <TableHead className="w-20 text-xs uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('scans')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Scans
                        {sortField === 'scans' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-24 text-xs uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Erstellt
                        {sortField === 'created_at' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-20 text-xs uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('health_status')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Status
                        {sortField === 'health_status' ? (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-28 text-xs uppercase tracking-wider">
                      Buch
                    </TableHead>
                    <TableHead className="w-28 text-xs uppercase tracking-wider">
                      Kampagnen
                    </TableHead>
                    <TableHead className="w-28 text-right text-xs uppercase tracking-wider">
                      Aktionen
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                    {sortedLinks.map((link, index) => (
                      <TableRow 
                        key={link.id}
                        className={`${
                          highlightId === link.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        } ${selectedLinks.has(link.id) ? 'bg-blue-50' : ''}`}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedLinks.has(link.id)}
                            onChange={() => handleSelectLink(link.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </TableCell>
                        <TableCell>
                          <QRCodeRenderer
                            data={link.redirect || ''}
                            size={32}
                            styleConfig={link.metadata?.styleConfig}
                            logoUrl={link.metadata?.styleConfig?.logoUrl}
                            className="w-8 h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {/* Error/Warning indicator dot */}
                            {(link.health_status === 'error' || link.health_status === 'warning') && (
                              <div className="relative group">
                                <div className={`w-2 h-2 rounded-full ${
                                  link.health_status === 'error' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
                                }`} />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                  <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                    {link.health_status === 'warning' 
                                      ? (link.metadata?.iframe_error || 'Einbettung nicht möglich')
                                      : (link.health_error || 'QR-Code nicht erreichbar')
                                    }
                                  </div>
                                  <div className="absolute left-1 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            )}
                            <span className="font-medium text-gray-900 truncate block max-w-[10rem]" title={link.title}>
                              {link.title}
                            </span>
                            {link.author_id && link.author_id !== user?.id && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                Team
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-[10rem]" title={link.slug}>
                            /{link.slug}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {getTypeIcon(link.type || 'website')}
                            <span className="text-sm text-gray-600 capitalize">
                              {link.type || 'website'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 truncate block max-w-[8rem]" title={link.target_url}>
                              {link.target_url}
                            </span>
                            <button
                              onClick={() => copyToClipboard(link.target_url)}
                              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
                              title="URL kopieren"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <BarChart3 className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {link.scans || 0}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {new Date(link.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {link.health_status && link.health_status !== 'unknown' ? (
                            link.health_status === 'healthy' ? (
                              <span className="inline-flex items-center text-xs text-green-600">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                OK
                              </span>
                            ) : link.health_status === 'warning' ? (
                              <span 
                                className="inline-flex items-center text-xs text-yellow-600 cursor-help" 
                                title={link.metadata?.iframe_error || link.health_error || 'Einbettung nicht möglich'}
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Warnung
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-xs text-red-600" title={link.health_error}>
                                <XCircle className="w-3 h-3 mr-1" />
                                Fehler
                              </span>
                            )
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {link.book_id ? (
                            <span className="inline-flex items-center text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                              <BookOpen className="w-3 h-3 mr-1" />
                              {getBookTitle(link.book_id) || 'Unbekannt'}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {link.campaign_id ? (
                            <span className="inline-flex items-center text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                              <Megaphone className="w-3 h-3 mr-1" />
                              {getCampaignTitle(link.campaign_id) || 'Unbekannt'}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {/* Email-Icon entfernt: E-Mails werden im Berichte-Bereich angezeigt */}
                            <button
                              onClick={async () => {
                                // Generate high-res QR code for download
                                if (link.redirect) {
                                  const { generateQRCode } = await import('../../utils/qrCodeHelper');
                                  const styleConfig = link.metadata?.styleConfig;
                                  const result = await generateQRCode({
                                    data: link.redirect,
                                    width: 1024,
                                    height: 1024,
                                    ...(styleConfig ? {
                                      dotsOptions: {
                                        type: styleConfig.dotsType,
                                        color: styleConfig.dotsColor,
                                        gradient: styleConfig.dotsGradient
                                      },
                                      cornersSquareOptions: {
                                        type: styleConfig.cornersSquareType,
                                        color: styleConfig.cornersColor
                                      },
                                      cornersDotOptions: {
                                        type: styleConfig.cornersDotType,
                                        color: styleConfig.cornersColor
                                      },
                                      backgroundOptions: {
                                        color: styleConfig.backgroundTransparent ? 'transparent' : styleConfig.backgroundColor
                                      },
                                      image: styleConfig.logoUrl,
                                      imageOptions: styleConfig.logoUrl ? {
                                        crossOrigin: 'anonymous',
                                        margin: styleConfig.logoMargin || 0,
                                        imageSize: (styleConfig.logoSize || 25) / 100,
                                        hideBackgroundDots: styleConfig.hideBackgroundDots
                                      } : undefined,
                                      qrOptions: { errorCorrectionLevel: styleConfig.errorCorrectionLevel || 'M' }
                                    } : {})
                                  });
                                  
                                  if (result.blob) {
                                    const { downloadQRCode } = await import('../../utils/qrCodeHelper');
                                    await downloadQRCode(result.blob, `qr-${link.slug}.png`);
                                  } else {
                                    // Fallback to backend URL
                                    if (link.png) {
                                      const { downloadQRCode } = await import('../../utils/qrCodeHelper');
                                      await downloadQRCode(link.png, `qr-${link.slug}.png`);
                                    }
                                  }
                                }
                              }}
                              className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
                              title="PNG herunterladen"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleTestLink(link)}
                              className="p-1 hover:bg-gray-100 rounded text-blue-600 hover:text-blue-900"
                              title="QR-Code Vorschau (zählt nicht als Scan)"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={async () => {
                                // Fetch fresh data from backend when editing
                                try {
                                  console.log('🔄 Fetching fresh data for edit:', link.id)
                                  const response = await fetch(`${API_BASE}/api/links/${link.id}`, {
                                    headers: {
                                      'Authorization': `Bearer ${session?.access_token}`
                                    }
                                  })
                                  
                                  if (response.ok) {
                                    const freshLink = await response.json()
                                    console.log('✅ Fresh link data:', freshLink)
                                    console.log('📄 PDF URL from backend:', freshLink.pdf_url)
                                    console.log('📝 Metadata from backend:', freshLink.metadata)
                                    setSelectedLink(freshLink)
                                    setIsEditMode(true)
                                    setShowModal(true)
                                    setCreateType(freshLink.type as any || 'website')
                                    setPdfUrl(freshLink.pdf_url || '')
                                    handleBrandingTypeChange(freshLink.branding_type || 'beyondpages')
                                    
                                    if (freshLink.metadata?.colorMode) {
                                      setColorMode(freshLink.metadata.colorMode)
                                    }
                                    if (freshLink.metadata?.customColors) {
                                      setCustomColors(freshLink.metadata.customColors)
                                    }
                                  } else {
                                    console.error('Failed to fetch link data')
                                    // Fall back to cached data
                                    setSelectedLink(link)
                                    setIsEditMode(true)
                                    setShowModal(true)
                                    setCreateType(link.type as any || 'website')
                                    setPdfUrl(link.pdf_url || '')
                                    handleBrandingTypeChange(link.branding_type || 'beyondpages')
                                    // Set custom colors if available (check both locations)
                                    if (link.custom_colors) {
                                      setCustomColors(link.custom_colors)
                                    } else if (link.metadata?.customColors) {
                                      setCustomColors(link.metadata.customColors)
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error fetching link:', error)
                                  // Fall back to cached data
                                  setSelectedLink(link)
                                  setIsEditMode(true)
                                  setShowModal(true)
                                  setCreateType(link.type as any || 'website')
                                  handleBrandingTypeChange(link.branding_type || 'beyondpages')
                                  // Set custom colors if available (check both locations)
                                  if (link.custom_colors) {
                                    setCustomColors(link.custom_colors)
                                  } else if (link.metadata?.customColors) {
                                    setCustomColors(link.metadata.customColors)
                                  }
                                }
                              }}
                              className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-gray-900"
                              title="Bearbeiten"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteLink(link.id)}
                              disabled={deleteLoading === link.id}
                              className="p-1 hover:bg-gray-100 rounded text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Löschen"
                            >
                              {deleteLoading === link.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedLinks.map(link => (
                <div 
                  key={link.id} 
                  className={`bg-white rounded-lg shadow-sm border ${
                    highlightId === link.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                  } overflow-hidden hover:shadow-md transition-shadow ${
                    selectedLinks.has(link.id) ? 'ring-2 ring-blue-200' : ''
                  }`}
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedLinks.has(link.id)}
                      onChange={() => handleSelectLink(link.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  {/* QR Code Preview */}
                  <div className="p-4 bg-gray-50 flex justify-center relative">
                    <QRCodeRenderer
                      data={link.redirect || ''}
                      size={128}
                      styleConfig={link.metadata?.styleConfig}
                      className="object-contain"
                      fallbackUrl={link.png}
                    />
                  </div>

                  {/* Link Details */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          {/* Error indicator dot - only show for established links, not new ones */}
                          {link.health_status === 'error' && link.created_at && 
                           new Date(link.created_at).getTime() < Date.now() - 60000 && (
                            <div className="relative group">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                  {link.health_error || 'QR-Code nicht erreichbar'}
                                </div>
                                <div className="absolute left-1 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                              </div>
                            </div>
                          )}
                          {link.title}
                          {link.author_id && link.author_id !== user?.id && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              Team
                            </span>
                          )}
                        </h3>
                      </div>
                      <div className="flex items-center gap-1">
                        {getTypeIcon(link.type || 'website')}
                        <button
                          onClick={async () => {
                            // Fetch fresh data from backend when editing
                            try {
                              console.log('🔄 Fetching fresh data for edit:', link.id)
                              const response = await fetch(`${API_BASE}/api/links/${link.id}`, {
                                headers: {
                                  'Authorization': `Bearer ${session?.access_token}`
                                }
                              })
                              
                              if (response.ok) {
                                const freshLink = await response.json()
                                console.log('✅ Fresh link data:', freshLink)
                                setSelectedLink(freshLink)
                                setIsEditMode(true)
                                setShowModal(true)
                                setCreateType(freshLink.type as any || 'website')
                                setPdfUrl(freshLink.pdf_url || '')
                                setBrandingType(freshLink.branding_type || 'beyondpages')
                                
                                if (freshLink.metadata?.colorMode) {
                                  setColorMode(freshLink.metadata.colorMode)
                                }
                                if (freshLink.metadata?.customColors) {
                                  setCustomColors(freshLink.metadata.customColors)
                                }
                              } else {
                                console.error('Failed to fetch link data')
                                // Fall back to cached data
                                setSelectedLink(link)
                                setIsEditMode(true)
                                setShowModal(true)
                                setCreateType(link.type as any || 'website')
                                setPdfUrl(link.pdf_url || '')
                                handleBrandingTypeChange(link.branding_type || 'beyondpages')
                                // Set custom colors if available (check both locations)
                                if (link.custom_colors) {
                                  setCustomColors(link.custom_colors)
                                } else if (link.metadata?.customColors) {
                                  setCustomColors(link.metadata.customColors)
                                }
                              }
                            } catch (error) {
                              console.error('Error fetching link:', error)
                              // Fall back to cached data
                              setSelectedLink(link)
                              setIsEditMode(true)
                              setShowModal(true)
                              setCreateType(link.type as any || 'website')
                              setBrandingType(link.branding_type || 'beyondpages')
                              // Set custom colors if available (check both locations)
                              if (link.custom_colors) {
                                setCustomColors(link.custom_colors)
                              } else if (link.metadata?.customColors) {
                                setCustomColors(link.metadata.customColors)
                              }
                            }
                          }}
                          className="p-1 hover:bg-gray-100 rounded ml-2"
                          title="Bearbeiten"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteLink(link.id)}
                          disabled={deleteLoading === link.id}
                          className="p-1 hover:bg-gray-100 rounded text-red-600 disabled:opacity-50"
                          title="Löschen"
                        >
                          {deleteLoading === link.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 truncate">
                      {link.target_url}
                    </p>

                    {/* Health Status */}
                    {link.health_status && link.health_status !== 'unknown' && (
                      <div className="mb-2">
                        {link.health_status === 'healthy' ? (
                          <span className="inline-flex items-center text-xs text-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Funktioniert
                          </span>
                        ) : link.health_status === 'warning' ? (
                          <span className="inline-flex items-center text-xs text-yellow-600">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Warnung: {link.metadata?.iframe_error || link.health_error || 'Einbettung nicht möglich'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs text-red-600">
                            <XCircle className="w-3 h-3 mr-1" />
                            Fehler: {link.health_error || 'Nicht erreichbar'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                      <span>{link.scans || 0} Scans</span>
                      <span>{new Date(link.created_at).toLocaleDateString('de-DE')}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {/* Show email icon for types that capture emails */}
                        {/* Email-Icon entfernt: E-Mails werden im Berichte-Bereich angezeigt */}
                        <button
                          onClick={() => link.png && downloadQRCode(link.png, `qr-${link.slug}.png`)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="PNG herunterladen"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => link.svg && downloadQRCode(link.svg, `qr-${link.slug}.svg`)}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm flex items-center justify-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          SVG
                        </button>
                        <button
                          onClick={() => handleTestLink(link)}
                          className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded text-sm flex items-center justify-center gap-2"
                          title="QR-Code Vorschau (zählt nicht als Scan)"
                        >
                          <Eye className="w-4 h-4" />
                          Vorschau
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!loading && sortedLinks.length === 0 && (
        <div className="text-center py-12">
          <QrCode className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'Keine Ergebnisse gefunden' : 'Noch keine QR-Codes erstellt'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm 
              ? 'Versuchen Sie es mit anderen Suchbegriffen.'
              : 'Erstellen Sie Ihren ersten QR-Code um loszulegen.'}
          </p>
          {!searchTerm && (
            <button
              onClick={handleCreateQRCode}
              className={`px-4 py-2 rounded-lg ${
                isQRLimitReached 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title={isQRLimitReached ? `Plan-Limit erreicht (${qrUsageInfo?.used}/${qrUsageInfo?.limit})` : undefined}
            >
              {isQRLimitReached ? 'Upgrade für mehr QR-Codes' : 'Ersten QR-Code erstellen'}
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div id="create-modal" className="bg-white rounded-lg w-full max-w-7xl h-[90vh] flex flex-col">
            {/* Always show header for consistency */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-6">
                <h2 className="text-xl font-bold">{isEditMode ? 'QR-Code bearbeiten' : 'Neuen QR-Code erstellen'}</h2>
                {!isEditMode && (
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      wizardStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      1
                    </span>
                    <div className={`w-16 h-0.5 ${wizardStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      wizardStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      2
                    </span>
                    <div className={`w-16 h-0.5 ${wizardStep >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      wizardStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      3
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  handleModalClose()
                  setAiGeneratedData(null)
                  setCurrentSlugInput('')
                  setCreateError(null) // Clear error on close
                  setCustomColors({
                    primaryColor: '#3B82F6',
                    secondaryColor: '#1F2937',
                    textColor: '#FFFFFF'
                  })
                  setColorMode('gradient')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Error display */}
            {createError && (
              <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800">{createError}</p>
                    {createError.includes('Slug') && (
                      <p className="text-xs text-red-600 mt-1">
                        Tipp: Ändern Sie den Slug im Abschnitt "URL-Einstellungen" weiter unten.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Modal content with proper scrolling */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {isEditMode ? (
                // For edit mode, use the full UnifiedQRForm
                <UnifiedQRForm
                type={createType}
                onTypeChange={isEditMode ? undefined : setCreateType}
                initialData={(() => {
                  if (isEditMode && selectedLink) {
                    console.log('🚀 Passing initialData to UnifiedQRForm:')
                    console.log('  - selectedLink:', selectedLink)
                    console.log('  - selectedLink.pdf_url:', selectedLink.pdf_url)
                    console.log('  - selectedLink.description:', selectedLink.description)
                    console.log('  - selectedLink.metadata:', selectedLink.metadata)
                    
                    const data = {
                      title: selectedLink.title,
                      description: selectedLink.description || selectedLink.metadata?.description,
                      target_url: selectedLink.target_url,
                      pdf_url: selectedLink.pdf_url,
                      slug: selectedLink.slug,
                      // Spread all metadata fields (includes showDirectly, welcomeScreen, etc.)
                      ...(selectedLink.metadata || {})
                    }
                    console.log('🔍 InitialData being passed to UnifiedQRForm:', data)
                    console.log('🔍 Data has pdf_url?', !!data.pdf_url, data.pdf_url)
                    return data
                  }
                  return aiGeneratedData?.qrData?.metadata || aiGeneratedData || {}
                })()}
                books={books}
                campaigns={campaigns}
                selectedBookId={isEditMode && selectedLink ? selectedLink.book_id : (bookFilter || selectedBookId)}
                selectedCampaignId={isEditMode && selectedLink ? selectedLink.campaign_id : (campaignFilter || selectedCampaignId)}
                currentAccountSlug={currentAccountSlug}
                brandingType={brandingType}
                onBrandingTypeChange={handleBrandingTypeChange}
                userBranding={userBranding}
                customColors={customColors}
                onCustomColorsChange={(colors) => {
                  console.log('🎨 Links - onCustomColorsChange called with:', colors)
                  setCustomColors(colors)
                }}
                colorMode={colorMode}
                onColorModeChange={setColorMode}
                canUploadPDF={canUploadPDF()}
                isEditMode={isEditMode}
                hideDesignTab={true}
                editingLink={isEditMode && selectedLink && selectedLink.redirect ? {
                  slug: selectedLink.slug,
                  redirect: selectedLink.redirect
                } : undefined}
                onSubmit={async (data: any, bookId?: string, campaignId?: string, slug?: string) => {
                  try {
                    setCreateError(null) // Clear any previous errors
                    const formData = new FormData()
                    
                    // Handle title based on type
                    let title = data.title || ''
                    if (createType === 'multishop') {
                      title = data.bookTitle || 'Multi-Shop Link'
                    } else if (createType === 'author') {
                      title = (data.name && data.name.trim())
                        ? `Autorenprofil: ${data.name.trim()}`
                        : 'Autorenprofil'
                    } else if (createType === 'sample') {
                      title = `Leseprobe: ${data.title || 'Unbekannt'}`
                    }
                    
                    formData.append('title', title)
                    formData.append('type', createType)
                    
                    // Handle URL based on type
                    if (['multishop', 'author'].includes(createType)) {
                      formData.append('target_url', window.location.origin) // Placeholder URL
                      formData.append('metadata', JSON.stringify(data.metadata || data))
                    } else if (createType === 'sample') {
                      // For sample, use the target_url that was already set in UnifiedQRForm
                      console.log('🎯 Sample type - data object:', data)
                      console.log('🎯 Sample type - data.target_url:', data.target_url)
                      console.log('🎯 Sample type - data.pdfUrl:', data.pdfUrl)
                      console.log('🎯 Sample type - data.pdf_url:', data.pdf_url)
                      const sampleTargetUrl = data.target_url || data.pdfUrl || data.pdf_url || ''
                      console.log('🎯 Sample type - final target_url:', sampleTargetUrl)
                      formData.append('target_url', sampleTargetUrl)
                      // Only send pdf_url if it exists
                      const pdfUrl = data.pdf_url || data.pdfUrl;
                      if (pdfUrl) formData.append('pdf_url', pdfUrl)
                      formData.append('metadata', JSON.stringify(data.metadata || data))
                    } else if (createType === 'pdf') {
                      // For PDF, include everything in metadata
                      console.log('PDF submission data:', data)
                      formData.append('target_url', data.pdf_url || '')
                      // Only send pdf_url if it exists
                      if (data.pdf_url) {
                        formData.append('pdf_url', data.pdf_url)
                      }
                      formData.append('description', data.description || '')
                      
                      // Include all PDF data in metadata
                      const pdfMetadata = {
                        ...(data.metadata || {}),
                        description: data.description || '',
                        colorMode: data.colorMode || 'gradient',
                        customColors: customColors,
                        // Don't spread all data to avoid overriding specific fields
                      }
                      formData.append('metadata', JSON.stringify(pdfMetadata))
                    } else {
                      formData.append('target_url', data.target_url || data.pdf_url || '')
                      if (data.pdf_url) {
                        formData.append('pdf_url', data.pdf_url)
                      }
                      if (data.description) {
                        formData.append('description', data.description)
                      }
                      // Add metadata for all types to include styleConfig
                      // The data object already has metadata with styleConfig added by UnifiedQRForm
                      if (data.metadata) {
                        formData.append('metadata', JSON.stringify(data.metadata))
                      }
                    }
                    
                    formData.append('branding_type', brandingType)
                    
                    // Add custom colors if using custom branding
                    if (brandingType === 'custom') {
                      formData.append('custom_colors', JSON.stringify(customColors))
                    }
                    
                    if (isEditMode && selectedLink) {
                      // Update existing link
                      formData.append('slug', selectedLink.slug)
                      if (bookId) {
                        formData.append('book_id', bookId)
                      }
                      if (campaignId) {
                        formData.append('campaign_id', campaignId)
                      }
                      // For edit mode, ensure metadata is included properly
                      if (['multishop', 'author', 'sample'].includes(createType)) {
                        // Already added above
                      } else if (createType === 'pdf') {
                        // For PDF in edit mode, ensure ALL metadata fields are included
                        // The data object from UnifiedQRForm already has complete metadata
                        formData.set('metadata', JSON.stringify(data.metadata || {})) // Use set to override
                      } else {
                        // For all other types, ensure metadata (including styleConfig) is preserved
                        // Merge existing metadata with new metadata from form
                        const existingMetadata = selectedLink?.metadata || {}
                        const newMetadata = data.metadata || {}
                        const mergedMetadata = { ...existingMetadata, ...newMetadata }
                        
                        // Always include the merged metadata to preserve styleConfig
                        formData.append('metadata', JSON.stringify(mergedMetadata))
                      }
                      await updateLink(formData)
                    } else {
                      // Create new link
                      if (slug) {
                        formData.append('slug', slug)
                      }
                      if (bookId) {
                        formData.append('book_id', bookId)
                      }
                      if (campaignId) {
                        formData.append('campaign_id', campaignId)
                      }
                      await createLink(formData)
                    }
                    handleModalClose()
                    setCurrentSlugInput('')
                  } catch (error) {
                    // Show error in UI, don't close modal
                    const errorMessage = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten'
                    setCreateError(errorMessage)
                    // Don't close the modal, let user fix the issue
                  }
                }}
              />
              ) : (
                // For create mode, show wizard steps
                <div className="flex flex-col h-full">
                  {wizardStep === 1 && (
                    // Step 1: Choose Content Type
                    <div className="flex flex-1 min-h-0 overflow-hidden">
                      {/* Left side: Form */}
                      <div className="flex-1 p-6 overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">Schritt 1: Inhaltstyp wählen</h3>
                        <p className="text-gray-600 mb-6">Wählen Sie den Typ des QR-Codes, den Sie erstellen möchten.</p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Website / URL */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'website' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="website" checked={createType === 'website'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-blue-500 p-2 rounded-lg">
                                  <Globe className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">Webseite / URL</h4>
                                  <p className="text-xs text-gray-600">Beliebige Webseite oder URL</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zu einer Webseite</p>
                            </div>
                          </label>

                          {/* Author Profile */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'author' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="author" checked={createType === 'author'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-purple-500 p-2 rounded-lg">
                                  <User className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">Autorenprofil</h4>
                                  <p className="text-xs text-gray-600">Persönliche Autorenvorstellung</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zum Autorenprofil</p>
                            </div>
                          </label>

                          {/* Book Sample */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'sample' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="sample" checked={createType === 'sample'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-orange-500 p-2 rounded-lg">
                                  <Book className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">Leseprobe</h4>
                                  <p className="text-xs text-gray-600">Digitale Buchvorschau</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zur PDF-Leseprobe</p>
                            </div>
                          </label>

                          {/* Multi-Shop */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'multishop' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="multishop" checked={createType === 'multishop'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-blue-600 p-2 rounded-lg">
                                  <ShoppingBag className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">Multi-Shop</h4>
                                  <p className="text-xs text-gray-600">Alle Händler auf einen Blick</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zu Shopping-Links</p>
                            </div>
                          </label>

                          {/* PDF / Additional Material */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'pdf' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="pdf" checked={createType === 'pdf'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-red-500 p-2 rounded-lg">
                                  <FileText className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">PDF / Zusatzmaterial</h4>
                                  <p className="text-xs text-gray-600">Begleitmaterial als PDF</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zum Download</p>
                            </div>
                          </label>

                          {/* Spotify */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'spotify' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="spotify" checked={createType === 'spotify'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-green-500 p-2 rounded-lg">
                                  <Music className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">Spotify Playlist</h4>
                                  <p className="text-xs text-gray-600">Buchsoundtrack zum Hören</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zu Spotify</p>
                            </div>
                          </label>

                          {/* YouTube */}
                          <label className={`bg-white border-2 rounded-lg overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-lg h-[120px] ${createType === 'youtube' ? 'border-blue-500 shadow-lg' : 'border-gray-200'}`}>
                            <input type="radio" name="type" value="youtube" checked={createType === 'youtube'} onChange={(e) => setCreateType(e.target.value as any)} className="sr-only" />
                            <div className="p-4">
                              <div className="flex items-center mb-2">
                                <div className="bg-red-600 p-2 rounded-lg">
                                  <Youtube className="w-5 h-5 text-white" />
                                </div>
                                <div className="ml-3">
                                  <h4 className="font-semibold text-base">YouTube</h4>
                                  <p className="text-xs text-gray-600">Book Trailer oder Video</p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 border-t">
                              <p className="text-xs text-gray-600">QR-Code führt zu YouTube</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {/* Right side: Visual Preview */}
                      <div className="w-1/2 bg-gray-50 p-6 overflow-y-auto border-l">
                        <div className="flex items-center justify-center min-h-full">
                          {createType && (
                            <div className="relative">
                              {/* Modern iPhone Frame with Dynamic Island */}
                              <div className="relative mx-auto rounded-[3rem] p-[3px] h-[618px] w-[300px] shadow-2xl"
                                   style={{
                                     background: 'linear-gradient(to bottom right, #9CA3AF, #D1D5DB, #9CA3AF)'
                                   }}>
                                {/* Inner black frame */}
                                <div className="bg-black rounded-[2.85rem] p-[8px] w-full h-full relative">
                                  {/* Dynamic Island - adjusted dimensions and position */}
                                  <div className="absolute top-[16px] left-1/2 transform -translate-x-1/2 w-[89px] h-[28px] bg-black rounded-[14px] z-20 overflow-hidden">
                                    {/* Camera lens detail */}
                                    <div className="absolute right-[8px] top-1/2 transform -translate-y-1/2 w-[12px] h-[12px]">
                                      <div className="absolute inset-0 bg-gray-900 rounded-full"></div>
                                      <div className="absolute inset-[2px] bg-gray-800 rounded-full"></div>
                                      <div className="absolute inset-[3px] bg-black rounded-full"></div>
                                      <div className="absolute top-[3px] right-[3px] w-[2px] h-[2px] bg-blue-900 rounded-full opacity-50"></div>
                                    </div>
                                  </div>
                                  
                                  {/* Screen with scrollable content */}
                                  <div className="rounded-[2.3rem] overflow-hidden w-full h-full bg-white relative">
                                    {/* Status Bar */}
                                    <div className="absolute top-0 left-0 right-0 z-10 bg-transparent pt-[14px] pb-1">
                                      <div className="flex justify-between items-center">
                                        <span className="text-[11.5px] font-semibold text-black ml-9">9:41</span>
                                        <div className="flex items-center gap-1 mr-5">
                                          {/* Cellular Signal */}
                                          <svg className="w-3.5 h-2.5" viewBox="0 0 17 12" fill="black">
                                            <rect x="1" y="6" width="3" height="6" rx="0.5"/>
                                            <rect x="6" y="4" width="3" height="8" rx="0.5"/>
                                            <rect x="11" y="2" width="3" height="10" rx="0.5"/>
                                            <rect x="16" y="0" width="3" height="12" rx="0.5" opacity="0.3"/>
                                          </svg>
                                          {/* WiFi */}
                                          <svg className="w-3.5 h-2.5" viewBox="0 0 15 11" fill="black">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M7.5 2.5C9.77817 2.5 11.867 3.35876 13.4149 4.75343L14.5 3.66829C12.6409 1.97761 10.1758 1 7.5 1C4.82416 1 2.35909 1.97761 0.5 3.66829L1.58507 4.75343C3.13305 3.35876 5.22183 2.5 7.5 2.5ZM7.5 5.5C8.91962 5.5 10.21 6.06817 11.1516 6.98374L12.2367 5.89861C10.9834 4.68326 9.32608 4 7.5 4C5.67392 4 4.01661 4.68326 2.76329 5.89861L3.84837 6.98374C4.78996 6.06817 6.08038 5.5 7.5 5.5ZM9.03033 8.46967C8.55086 8.17453 8.04301 8 7.5 8C6.95699 8 6.44914 8.17453 5.96967 8.46967L7.5 10L9.03033 8.46967Z"/>
                                          </svg>
                                          {/* Battery */}
                                          <svg className="w-5 h-2.5" viewBox="0 0 25 12" fill="none">
                                            <rect x="1" y="2" width="19" height="8" stroke="black" strokeWidth="1" rx="2" fill="none"/>
                                            <rect x="2" y="3" width="17" height="6" fill="black" rx="1"/>
                                            <path d="M21 4V8C21.7 8 22.5 7.3284 22.5 6C22.5 4.6716 21.7 4 21 4Z" fill="black"/>
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  {/* Demo Preview */}
                                  <div className="w-full h-full pt-[44px] flex items-center justify-center bg-gray-100">
                                    {createType === 'spotify' && (
                                      <div className="w-full h-full overflow-y-auto scrollbar-hide" style={{ background: 'linear-gradient(to bottom right, #4F46E5, #7C3AED)', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                        {/* Spotify Demo Content */}
                                        <div className="min-h-full relative">
                                          {/* Header */}
                                          <div className="relative z-10">
                                            <div className="px-4 py-4">
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-white">
                                                  <span className="font-bold text-sm">Max Mustermann GmbH</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Main Content */}
                                          <div className="relative z-10 px-3 pb-4">
                                            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-3 shadow-2xl">
                                              {/* Book Cover Section */}
                                              <div className="flex items-start gap-3 mb-3">
                                                <div className="relative flex-shrink-0">
                                                  <img
                                                    src="/images/der-rabe-und-andere-gedichte-taschenbuch-edgar-allan-poe.webp"
                                                    alt="Der Rabe und andere Gedichte"
                                                    className="w-20 h-32 object-cover rounded-lg shadow-xl"
                                                  />
                                                  <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1 shadow-lg">
                                                    <Sparkles className="w-2 h-2" />
                                                  </div>
                                                </div>
                                                
                                                <div className="text-white flex-1">
                                                  <h2 className="text-sm font-bold mb-0.5">
                                                    Entdecke die Playlist
                                                  </h2>
                                                  <p className="text-white/80 text-xs mb-1">zu "Der Rabe und andere Gedichte"</p>
                                                  <div className="flex items-center gap-1 text-white/60 text-xs">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
                                                    </svg>
                                                    <span>Begleitende Playlist</span>
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Spotify Player Section */}
                                              <div>
                                                <h1 className="text-sm font-bold text-white mb-2 flex items-center justify-center gap-2">
                                                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                                  </svg>
                                                  Spotify Playlist
                                                </h1>

                                                {/* Spotify Embed Placeholder */}
                                                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-1 mb-3">
                                                  <div className="bg-gray-900 rounded-lg h-[120px] flex items-center justify-center">
                                                    <div className="text-center">
                                                      <svg className="w-16 h-16 text-green-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                                      </svg>
                                                      <p className="text-white text-xs">Classical Essentials</p>
                                                    </div>
                                                  </div>
                                                </div>

                                                {/* Description */}
                                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2.5 mb-2.5">
                                                  <p className="text-white/90 text-xs leading-relaxed">
                                                    Diese Playlist wurde sorgfältig zusammengestellt, um das Leseerlebnis der düsteren Gedichte von Edgar Allan Poe zu bereichern.
                                                  </p>
                                                </div>

                                                {/* Action Button */}
                                                <button className="w-full bg-green-500 text-white px-3 py-2 rounded-full font-medium text-xs flex items-center justify-center gap-1.5 shadow-lg">
                                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                                  </svg>
                                                  Auf Spotify anhören
                                                </button>
                                              </div>

                                              {/* Footer */}
                                              <div className="mt-3 text-center">
                                                <p className="text-white/60 text-xs">
                                                  Powered by Max Mustermann GmbH
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {createType === 'multishop' && (
                                      <div className="w-full h-full overflow-y-auto scrollbar-hide bg-gray-50" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                        {/* Multi-Shop Demo Content */}
                                        {/* Header with Branding */}
                                        <div className="bg-white shadow-sm">
                                          <div className="px-4 py-3">
                                            <div className="flex items-center justify-center">
                                              <span className="text-sm font-semibold text-blue-600">
                                                Max Mustermann GmbH
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Main Content */}
                                        <div className="px-4 py-4">
                                          {/* Book Info */}
                                          <div className="bg-white rounded-2xl shadow-xl p-3 mb-3">
                                            <div className="flex gap-2 mb-2">
                                              <img 
                                                src="/images/escoffier-und-die-nouvelle-cuisine-1-auflage-pfanneberg.webp" 
                                                alt="Escoffier und die Nouvelle Cuisine"
                                                className="w-20 h-26 rounded-lg shadow-lg object-cover"
                                              />
                                              <div className="flex-1">
                                                <h1 className="text-sm font-bold text-gray-900 mb-0.5">
                                                  Escoffier und die Nouvelle Cuisine
                                                </h1>
                                                <p className="text-xs text-gray-600">von Auguste Escoffier</p>
                                                <p className="text-xs text-gray-500 mt-1">ISBN: 978-3-86820-344-7</p>
                                              </div>
                                            </div>
                                            <p className="text-xs text-gray-700 leading-relaxed">Das Standardwerk der französischen Haute Cuisine.</p>
                                          </div>

                                          {/* Shop Selection */}
                                          <div className="bg-white rounded-2xl shadow-xl p-3">
                                            <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                                              </svg>
                                              Wählen Sie Ihren Buchhändler
                                            </h2>

                                            {/* Shop List - Condensed */}
                                            <div className="space-y-1.5">
                                              {[
                                                { name: 'Amazon', price: '29.95', available: true },
                                                { name: 'Thalia', price: '29.95', available: true },
                                                { name: 'Buchhandlung am Markt', price: '29.95', available: true, isLocal: true },
                                                { name: 'Hugendubel', price: '28.99', available: true },
                                                { name: 'Weltbild', price: '27.99', available: false }
                                              ].map((shop, index) => (
                                                <div key={index} className="bg-gray-50 rounded-lg p-2">
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                      <div className="w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center">
                                                        {shop.isLocal ? (
                                                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                                          </svg>
                                                        ) : (
                                                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                                          </svg>
                                                        )}
                                                      </div>
                                                      <div>
                                                        <h3 className="font-semibold text-xs text-gray-900">{shop.name}</h3>
                                                        <p className={`text-xs ${shop.available ? 'text-green-600' : 'text-red-600'}`}>
                                                          {shop.available ? 'Sofort lieferbar' : 'Nicht verfügbar'}
                                                        </p>
                                                      </div>
                                                    </div>
                                                    <span className="text-xs font-bold text-blue-600">
                                                      {shop.price} €
                                                    </span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>

                                            {/* Price Note */}
                                            <div className="mt-3 text-center text-xs text-gray-600">
                                              <p>Preise können variieren.</p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {createType === 'author' && (
                                      <div className="w-full h-full overflow-y-auto scrollbar-hide bg-gray-50" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                        {/* Author Profile Demo Content */}
                                        {/* Header with Branding */}
                                        <div className="bg-white shadow-sm">
                                          <div className="px-4 py-3">
                                            <div className="flex items-center justify-center">
                                              <span className="text-sm font-semibold" style={{ color: '#4F46E5' }}>
                                                Max Mustermann GmbH
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Main Content */}
                                        <div className="px-4 py-3">
                                          {/* Profile Header */}
                                          <div className="bg-white rounded-lg shadow-xl p-4 mb-3">
                                            <div className="flex flex-col items-center text-center">
                                              <img
                                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Goethe_%28Stieler_1828%29.jpg/440px-Goethe_%28Stieler_1828%29.jpg"
                                                alt="Johann Wolfgang von Goethe"
                                                className="w-20 h-20 rounded-full object-cover shadow-lg mb-3"
                                              />
                                              
                                              <h1 className="text-base font-bold text-gray-900 mb-1">Johann Wolfgang von Goethe</h1>
                                              <p className="text-xs text-gray-600 mb-2">Deutscher Dichter, Naturforscher und Staatsmann</p>
                                              
                                              {/* Social Links */}
                                              <div className="flex gap-1.5">
                                                {[
                                                  <svg key="globe" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>,
                                                  <svg key="insta" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/></svg>,
                                                  <svg key="fb" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
                                                  <svg key="x" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                                ].map((icon, index) => (
                                                  <div key={index} className="p-1.5 bg-gray-100 rounded-lg">
                                                    {icon}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          </div>

                                          {/* Biography */}
                                          <div className="bg-white rounded-lg shadow-xl p-4 mb-3">
                                            <h2 className="text-sm font-bold text-gray-900 mb-2">Biografie</h2>
                                            <div className="text-xs text-gray-700 space-y-1.5">
                                              <p>Johann Wolfgang von Goethe (1749-1832) war einer der bedeutendsten deutschen Dichter und Denker.</p>
                                              <p>Seine Werke, darunter "Faust" und "Die Leiden des jungen Werthers", gehören zu den wichtigsten der Weltliteratur.</p>
                                            </div>
                                          </div>

                                          {/* Awards */}
                                          <div className="bg-white rounded-lg shadow-xl p-4 mb-3">
                                            <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1">
                                              <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                              </svg>
                                              Auszeichnungen
                                            </h2>
                                            <ul className="space-y-1">
                                              <li className="flex items-start gap-1 text-xs">
                                                <span className="text-yellow-500 mt-0.5">•</span>
                                                <span className="text-gray-700">Geadelt durch Kaiser Joseph II. (1782)</span>
                                              </li>
                                              <li className="flex items-start gap-1 text-xs">
                                                <span className="text-yellow-500 mt-0.5">•</span>
                                                <span className="text-gray-700">Mitglied der Leopoldina (1818)</span>
                                              </li>
                                            </ul>
                                          </div>

                                          {/* Selected Works */}
                                          <div className="bg-white rounded-lg shadow-xl p-4">
                                            <h2 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                                              </svg>
                                              Ausgewählte Werke
                                            </h2>
                                            <div className="space-y-1">
                                              {[
                                                { title: 'Faust I & II', year: '1808/1832' },
                                                { title: 'Die Leiden des jungen Werthers', year: '1774' }
                                              ].map((book, index) => (
                                                <div key={index} className="bg-gray-50 p-2 rounded-lg">
                                                  <h3 className="font-medium text-xs text-gray-900">{book.title}</h3>
                                                  <p className="text-xs text-gray-600">{book.year}</p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {createType === 'sample' && (
                                      <div className="w-full h-full overflow-y-auto scrollbar-hide bg-gradient-to-br from-blue-50 to-indigo-50" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                        {/* Book Sample Demo Content */}
                                        {/* Header */}
                                        <div className="bg-white shadow-sm">
                                          <div className="px-4 py-3">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-blue-600">
                                                  Max Mustermann GmbH
                                                </span>
                                              </div>
                                              <div className="p-1.5 bg-gray-100 rounded-lg">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                                </svg>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Book Info */}
                                        <div className="px-4 py-3">
                                          <div className="bg-white rounded-lg shadow-xl p-3 mb-3">
                                            <div className="flex gap-3 mb-3">
                                              <img
                                                src="/images/der-rabe-und-andere-gedichte-taschenbuch-edgar-allan-poe.webp"
                                                alt="Der Rabe und andere Gedichte"
                                                className="w-26 h-36 rounded-lg shadow-lg object-cover"
                                              />
                                              
                                              <div className="flex-1">
                                                <h1 className="text-sm font-bold text-gray-900 mb-0.5">Der Rabe und andere Gedichte</h1>
                                                <p className="text-xs text-gray-700 mb-1">von Edgar Allan Poe</p>
                                                
                                                <div className="space-y-0.5 text-xs">
                                                  <div>
                                                    <span className="text-gray-500">Genre:</span>
                                                    <span className="ml-1 font-medium">Lyrik / Horror</span>
                                                  </div>
                                                  <div className="flex items-center gap-1">
                                                    <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                                                    </svg>
                                                    <span className="font-medium">Max Mustermann Verlag</span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="mb-2">
                                              <p className="text-xs text-gray-700 leading-relaxed">Eine Sammlung der berühmtesten Gedichte von Edgar Allan Poe, darunter "Der Rabe".</p>
                                            </div>

                                            <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                                              <div className="flex items-center gap-1">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                </svg>
                                                <span>Erste 3 Kapitel</span>
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                </svg>
                                                <span>ca. 15 Min.</span>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Additional Info */}
                                          <div className="bg-white rounded-lg shadow-xl p-3 mb-3">
                                            <div className="grid grid-cols-2 gap-3 text-xs">
                                              <div>
                                                <span className="text-gray-500 flex items-center gap-1">
                                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                                  </svg>
                                                  Erschienen:
                                                </span>
                                                <p className="font-medium mt-0.5">29.01.1845</p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500 flex items-center gap-1">
                                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                                                  </svg>
                                                  ISBN:
                                                </span>
                                                <p className="font-medium mt-0.5">978-3-12345-678-9</p>
                                              </div>
                                            </div>
                                          </div>

                                          {/* PDF Viewer Preview */}
                                          <div className="bg-white rounded-lg shadow-xl overflow-hidden mb-3">
                                            <div className="bg-gray-100 px-2 py-1.5 flex items-center justify-between">
                                              <div className="flex items-center gap-1">
                                                <div className="p-0.5 bg-gray-200 rounded opacity-50">
                                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                                                  </svg>
                                                </div>
                                                <span className="text-xs">Seite 1 von ...</span>
                                                <div className="p-0.5 bg-gray-200 rounded">
                                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                                  </svg>
                                                </div>
                                              </div>
                                              
                                              <div className="flex items-center gap-1 text-xs">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"/>
                                                </svg>
                                                <span>100%</span>
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"/>
                                                </svg>
                                              </div>
                                            </div>
                                            
                                            <div className="relative bg-gray-50" style={{ height: '140px' }}>
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="text-center">
                                                  <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                  </svg>
                                                  <p className="text-gray-500 text-xs">PDF Leseprobe</p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Action Button */}
                                          <button className="w-full bg-blue-600 text-white px-3 py-2 rounded-lg font-medium text-xs">
                                            Leseprobe herunterladen
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {(createType === 'website' || createType === 'pdf' || createType === 'youtube') && (
                                      <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                                        <div className="text-center p-6">
                                          <div className="mb-4">
                                            {createType === 'website' && <Globe className="w-16 h-16 mx-auto text-blue-600" />}
                                            {createType === 'pdf' && <FileText className="w-16 h-16 mx-auto text-blue-600" />}
                                            {createType === 'youtube' && <Youtube className="w-16 h-16 mx-auto text-red-600" />}
                                          </div>
                                          <h3 className="text-lg font-semibold mb-2">
                                            {createType === 'website' && 'Webseite / URL'}
                                            {createType === 'pdf' && 'PDF / Zusatzmaterial'}
                                            {createType === 'youtube' && 'YouTube'}
                                          </h3>
                                          <p className="text-gray-600 text-sm">
                                            Die Vorschau wird nach der Konfiguration angezeigt
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Home Indicator */}
                                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-[134px] h-[5px] bg-black rounded-full"></div>
                                </div>
                              </div>
                              
                              {/* iPhone Side Buttons */}
                              <div className="absolute top-0 inset-x-0">
                                {/* Silent Switch */}
                                <div className="h-[32px] w-[3px] bg-gray-400 absolute left-[-5px] top-[74px] rounded-l-lg"></div>
                                {/* Volume Up */}
                                <div className="h-[46px] w-[3px] bg-gray-400 absolute left-[-5px] top-[126px] rounded-l-lg"></div>
                                {/* Volume Down */}
                                <div className="h-[46px] w-[3px] bg-gray-400 absolute left-[-5px] top-[180px] rounded-l-lg"></div>
                                {/* Power Button */}
                                <div className="h-[64px] w-[3px] bg-gray-400 absolute right-[-5px] top-[144px] rounded-r-lg"></div>
                              </div>
                            </div>
                            </div>
                          )}
                          {!createType && (
                            <p className="text-gray-500">Wählen Sie einen Inhaltstyp aus</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {wizardStep === 2 && (
                    // Step 2: Define Context & Metadata
                    <div className="h-full">
                      <UnifiedQRForm
                        type={createType}
                        onTypeChange={undefined} // No type change in step 2
                        initialData={wizardFormData}
                        books={books}
                        campaigns={campaigns}
                        selectedBookId={bookFilter || selectedBookId}
                        selectedCampaignId={campaignFilter || selectedCampaignId}
                        onSelectedBookIdChange={setSelectedBookId}
                        onSelectedCampaignIdChange={setSelectedCampaignId}
                        currentAccountSlug={currentAccountSlug}
                        brandingType={brandingType}
                        onBrandingTypeChange={handleBrandingTypeChange}
                        userBranding={userBranding}
                        customColors={customColors}
                        onCustomColorsChange={(colors) => {
                          console.log('🎨 Links - onCustomColorsChange called with:', colors)
                          setCustomColors(colors)
                        }}
                        colorMode={colorMode}
                        onColorModeChange={setColorMode}
                        canUploadPDF={canUploadPDF()}
                        isEditMode={false}
                        hideDesignTab={true} // Hide design tab in step 2
                        onBack={() => setWizardStep(1)} // Go back to step 1
                        onSubmit={async (data) => {
                          console.log('🔵 Links - Step 2 onSubmit called with data:', data)
                          console.log('🔵 Links - Data keys:', Object.keys(data))
                          console.log('🔵 Links - Data has fonts?', 'fonts' in data)
                          console.log('🔵 Links - Data has textSizes?', 'textSizes' in data)
                          console.log('🔵 Links - Data has isbn?', 'isbn' in data)
                          console.log('🔵 Links - Data has publishDate?', 'publishDate' in data)
                          console.log('🔵 Links - Full data structure:', JSON.stringify(data, null, 2))
                          // Store form data and move to step 3
                          updateWizardFormData(data)
                          console.log('🔵 Links - wizardFormData set to:', data)
                          setWizardStep(3)
                          console.log('🔵 Links - Moving to step 3')
                        }}
                      />
                    </div>
                  )}
                  
                  {wizardStep === 3 && (
                    // Step 3: Select QR Code Design
                    <div className="h-full">
                      {console.log('🟣 Links - Rendering step 3 with wizardFormData:', wizardFormData)}
                      {console.log('🟣 Links - wizardFormData keys:', Object.keys(wizardFormData || {}))}
                      {console.log('🟣 Links - wizardFormData has fonts?', wizardFormData && 'fonts' in wizardFormData)}
                      {console.log('🟣 Links - wizardFormData has textSizes?', wizardFormData && 'textSizes' in wizardFormData)}
                      <UnifiedQRForm
                        type={createType}
                        onTypeChange={undefined} // No type change in step 3
                        initialData={wizardFormData}
                        books={books}
                        campaigns={campaigns}
                        selectedBookId={bookFilter || selectedBookId}
                        selectedCampaignId={campaignFilter || selectedCampaignId}
                        onSelectedBookIdChange={setSelectedBookId}
                        onSelectedCampaignIdChange={setSelectedCampaignId}
                        currentAccountSlug={currentAccountSlug}
                        brandingType={brandingType}
                        onBrandingTypeChange={handleBrandingTypeChange}
                        userBranding={userBranding}
                        customColors={customColors}
                        onCustomColorsChange={(colors) => {
                          console.log('🎨 Links - onCustomColorsChange called with:', colors)
                          setCustomColors(colors)
                        }}
                        canUploadPDF={canUploadPDF()}
                        isEditMode={false}
                        hideContentTab={true} // Hide content tab in step 3
                        defaultTab="design" // Start with design tab
                        onBack={() => setWizardStep(2)} // Go back to step 2
                        onSubmit={async (data: any, bookId?: string, campaignId?: string, slug?: string) => {
                          try {
                            setCreateError(null) // Clear any previous errors
                            const formData = new FormData()
                            
                            // Handle title based on type
                            let title = data.title || ''
                            if (createType === 'multishop') {
                              title = data.bookTitle || 'Multi-Shop Link'
                            } else if (createType === 'author') {
                              title = (data.name && data.name.trim())
                                ? `Autorenprofil: ${data.name.trim()}`
                                : 'Autorenprofil'
                            } else if (createType === 'sample') {
                              title = `Leseprobe: ${data.title || 'Unbekannt'}`
                            }
                            
                            formData.append('title', title)
                            formData.append('type', createType)
                            
                            // Handle URL based on type
                            console.log('🎯 URL handling - createType:', createType)
                            console.log('🎯 URL handling - includes check:', ['multishop', 'author'].includes(createType))
                            if (['multishop', 'author'].includes(createType)) {
                              formData.append('target_url', window.location.origin) // Placeholder URL
                              
                              // Always use the metadata property if it exists, otherwise construct it from data
                              let metadataToSend;
                              if (data.metadata) {
                                metadataToSend = data.metadata;
                              } else {
                                // Construct metadata based on type
                                if (createType === 'multishop') {
                                  metadataToSend = {
                                    bookTitle: data.bookTitle || data.title || '',
                                    author: data.author || '',
                                    isbn: data.isbn || '',
                                    coverUrl: data.coverUrl || '',
                                    publishDate: data.publishDate || '',
                                    description: data.description || '',
                                    shops: data.shops || [],
                                    primaryShop: data.primaryShop || '',
                                    defaultCurrency: data.defaultCurrency || 'EUR',
                                    fonts: data.fonts || { heading: 'Inter', body: 'Inter' },
                                    textSizes: data.textSizes || {},
                                    companyPosition: data.companyPosition || 'bottom',
                                    customColors: data.customColors,
                                    colorMode: data.colorMode,
                                    styleConfig: data.styleConfig
                                  };
                                } else {
                                  metadataToSend = data;
                                }
                              }
                              
                              console.log('🛍️ Multishop submission to backend:', {
                                createType,
                                hasMetadata: !!data.metadata,
                                dataKeys: Object.keys(data),
                                metadataToSend,
                                hasRequiredFields: createType === 'multishop' ? {
                                  bookTitle: !!(metadataToSend.bookTitle || metadataToSend.title),
                                  author: !!metadataToSend.author,
                                  shops: !!metadataToSend.shops
                                } : null
                              })
                              formData.append('metadata', JSON.stringify(metadataToSend))
                            } else if (createType === 'sample') {
                              // For sample, target_url should be the PDF URL
                              const sampleTargetUrl = data.target_url || data.pdfUrl || data.pdf_url || ''
                              console.log('📚 Sample submission - target_url:', sampleTargetUrl)
                              formData.append('target_url', sampleTargetUrl)
                              // Only send pdf_url if it exists
                              const pdfUrl = data.pdf_url || data.pdfUrl;
                              if (pdfUrl) {
                                formData.append('pdf_url', pdfUrl);
                              }
                              if (data.description) {
                                formData.append('description', data.description)
                              }
                              formData.append('metadata', JSON.stringify(data.metadata || data))
                            } else if (createType === 'pdf') {
                              // For PDF, include everything in metadata
                              formData.append('target_url', data.pdf_url || '')
                              // Only send pdf_url if it exists
                              if (data.pdf_url) {
                                formData.append('pdf_url', data.pdf_url)
                              }
                              
                              // Include all PDF data in metadata
                              const pdfMetadata = {
                                ...(data.metadata || {}),
                                description: data.description || '',
                                colorMode: data.colorMode || 'gradient',
                                customColors: customColors,
                                // Don't spread all data to avoid overriding specific fields
                              }
                              formData.append('metadata', JSON.stringify(pdfMetadata))
                            } else {
                              formData.append('target_url', data.target_url || data.pdf_url || '')
                              if (data.pdf_url) {
                                formData.append('pdf_url', data.pdf_url)
                              }
                              if (data.description) {
                                formData.append('description', data.description)
                              }
                              // Add metadata for all types to include styleConfig
                              if (data.metadata) {
                                formData.append('metadata', JSON.stringify(data.metadata))
                              }
                            }
                            
                            formData.append('branding_type', brandingType)
                            
                            // Add custom colors if using custom branding
                            if (brandingType === 'custom') {
                              formData.append('custom_colors', JSON.stringify(customColors))
                            }
                            
                            // Create new link
                            if (slug) {
                              formData.append('slug', slug)
                            }
                            if (bookId) {
                              formData.append('book_id', bookId)
                            }
                            if (campaignId) {
                              formData.append('campaign_id', campaignId)
                            }
                            await createLink(formData)
                            
                            handleModalClose()
                            setCurrentSlugInput('')
                          } catch (error) {
                            // Show error in UI, don't close modal
                            const errorMessage = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten'
                            setCreateError(errorMessage)
                            // Don't close the modal, let user fix the issue
                          }
                        }}
                      />
                    </div>
                  )}

                  {wizardStep === 4 && newlyCreatedLink && (
                    // Step 4: Success - Download QR Code
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        QR-Code erfolgreich erstellt!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Ihr QR-Code ist einsatzbereit. Sie können ihn jetzt herunterladen und verwenden.
                      </p>

                      {/* QR Code Preview */}
                      <div className="mb-6">
                        <img
                          src={newlyCreatedLink.png}
                          alt="QR Code"
                          className="w-48 h-48 mx-auto border-2 border-gray-200 rounded-lg"
                        />
                      </div>

                      {/* Download Buttons */}
                      <div className="flex gap-3 mb-6">
                        <a
                          href={newlyCreatedLink.png}
                          download={`qr-${newlyCreatedLink.slug}.png`}
                          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Als PNG herunterladen
                        </a>
                        <a
                          href={newlyCreatedLink.svg}
                          download={`qr-${newlyCreatedLink.slug}.svg`}
                          className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Als SVG herunterladen
                        </a>
                      </div>

                      {/* QR URL */}
                      <div className="w-full max-w-md mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          QR-Code URL:
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            readOnly
                            value={newlyCreatedLink.redirect}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                            onClick={(e) => e.currentTarget.select()}
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(newlyCreatedLink.redirect)
                              toast.success('URL kopiert!')
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Kopieren
                          </button>
                        </div>
                      </div>

                      {/* Close Button */}
                      <button
                        onClick={() => {
                          handleModalClose()
                          setNewlyCreatedLink(null)
                          setWizardStep(1)
                          clearWizardData() // Clear wizard data when closing
                        }}
                        className="px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Fertig
                      </button>
                    </div>
                  )}

                  {/* Navigation buttons for wizard */}
                  {!isEditMode && wizardStep === 1 && (
                    <div className="bg-white border-t px-6 py-3">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => {
                            console.log('🟢 Links - Next button clicked, moving from step 1 to 2')
                            console.log('🟢 Links - Current createType:', createType)
                            // Preserve the selected type and any existing data when moving to step 2
                            const newData = { ...wizardFormData, type: createType }
                            console.log('🟢 Links - Setting wizardFormData to:', newData)
                            setWizardFormData(newData)
                            setWizardStep(2)
                          }}
                          disabled={!createType}
                          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Weiter
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {!isEditMode && wizardStep === 2 && (
                    <div className="bg-white border-t px-6 py-3">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setWizardStep(1)}
                          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Zurück
                        </button>
                        {/* Note: The "Weiter" button for step 2 is handled by the UnifiedQRForm's submit button */}
                      </div>
                    </div>
                  )}
                  
                  {!isEditMode && wizardStep === 3 && (
                    <div className="bg-white border-t px-6 py-3">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setWizardStep(2)}
                          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Zurück
                        </button>
                        {/* Note: The "QR-Code erstellen" button for step 3 is handled by the UnifiedQRForm's submit button */}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* AI Generated Info */}
      {aiGeneratedData && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm font-medium text-blue-900 mb-2">
            🤖 AI-generierte Idee
          </p>
          {aiGeneratedData.aiContent && (
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Mehrwert:</strong> {aiGeneratedData.aiContent.reader_value}</p>
              <p><strong>Umsetzung:</strong> {aiGeneratedData.aiContent.implementation}</p>
            </div>
          )}
        </div>
      )}

      {/* Email Captures Modal */}
      {showEmailModal && selectedEmailLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Erfasste E-Mail-Adressen</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedEmailLink.title} ({selectedEmailLink.type})
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEmailModal(false)
                  setSelectedEmailLink(null)
                  setEmailCaptures([])
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {emailsLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-600">Lade E-Mail-Adressen...</p>
              </div>
            ) : emailCaptures.length > 0 ? (
              <>
                <div className="mb-4 flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    {emailCaptures.length} E-Mail-Adresse{emailCaptures.length !== 1 ? 'n' : ''} erfasst
                  </p>
                  <button
                    onClick={() => {
                      // Export emails as CSV
                      const csv = [
                        'Email,Erfasst am,Quelle',
                        ...emailCaptures.map(e => 
                          `"${e.email}","${new Date(e.created_at).toLocaleString('de-DE')}","${e.metadata?.source || 'Unbekannt'}"`
                        )
                      ].join('\n')
                      
                      const blob = new Blob([csv], { type: 'text/csv' })
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `emails-${selectedEmailLink.slug}.csv`
                      document.body.appendChild(a)
                      a.click()
                      window.URL.revokeObjectURL(url)
                      document.body.removeChild(a)
                    }}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Download className="w-4 h-4" />
                    Als CSV exportieren
                  </button>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Erfasst am</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quelle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {emailCaptures.map((capture) => (
                        <tr key={capture.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{capture.email}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {new Date(capture.created_at).toLocaleString('de-DE')}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {capture.metadata?.source === 'book_sample_gate' ? 'Leseprobe' :
                             capture.metadata?.source === 'author_profile_newsletter' ? 'Autorenprofil' :
                             capture.metadata?.source || 'Unbekannt'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">Noch keine E-Mail-Adressen erfasst</p>
                <p className="text-sm text-gray-500 mt-1">
                  E-Mail-Adressen werden erfasst, wenn Besucher sich für Newsletter anmelden oder auf geschützte Inhalte zugreifen.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Sprungblatt Partnership Modal */}
      {showSprungblattModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSprungblattModal(false)
            }
          }}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Erweiterte Web-Apps für Ihre QR-Codes</h3>
                <button
                  onClick={() => setShowSprungblattModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <svg className="w-8 h-8 text-purple-700" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 3L21 21M21 3L3 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Sprungblatt</h4>
                    <p className="text-sm text-gray-600">Digitalagentur & Partner von BeyondPages</p>
                  </div>
                </div>

                <div className="space-y-3 text-gray-600">
                  <p>
                    Benötigen Sie individuelle Funktionen für Ihre QR-Codes? Sprungblatt entwickelt maßgeschneiderte Web-Apps, die perfekt in BeyondPages integriert werden.
                  </p>
                  
                  <div className="space-y-2">
                    <h5 className="font-semibold text-gray-900">Unsere Leistungen:</h5>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Interaktive Gewinnspiele & Umfragen</li>
                      <li>Augmented Reality Erlebnisse</li>
                      <li>Personalisierte Leser-Journeys</li>
                      <li>Event-Registrierungen & Ticketing</li>
                      <li>Individuelle Buchvorschau-Systeme</li>
                      <li>Analytics & Tracking-Lösungen</li>
                    </ul>
                  </div>
                </div>
              </div>

              {!sprungblattFormData.name && !sprungblattFormData.email ? (
                <button
                  onClick={() => setSprungblattFormData({ ...sprungblattFormData, name: 'show-form' })}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Kontakt aufnehmen
                </button>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  setSprungblattSubmitting(true)
                  
                  try {
                    const requestData = {
                      ...sprungblattFormData,
                      userId: user?.id,
                      userEmail: user?.email
                    }
                    
                    console.log('📤 Sending Sprungblatt form:', {
                      url: `${API_BASE}/api/contact/sprungblatt`,
                      hasAuth: !!session?.access_token,
                      data: requestData
                    })
                    
                    // Send form to backend
                    const response = await fetch(`${API_BASE}/api/contact/sprungblatt`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session?.access_token}`
                      },
                      body: JSON.stringify(requestData)
                    })

                    if (response.ok) {
                      alert('Vielen Dank für Ihre Anfrage! Sprungblatt wird sich in Kürze bei Ihnen melden.')
                      setShowSprungblattModal(false)
                      setSprungblattFormData({ name: '', email: '', company: '', message: '' })
                    } else {
                      const errorData = await response.json().catch(() => null)
                      console.error('Sprungblatt form error:', {
                        status: response.status,
                        statusText: response.statusText,
                        error: errorData
                      })
                      alert(`Fehler beim Senden der Anfrage: ${errorData?.error || response.statusText}`)
                    }
                  } catch (error) {
                    console.error('Error submitting Sprungblatt form:', error)
                    alert('Fehler beim Senden der Anfrage. Bitte versuchen Sie es später erneut.')
                  } finally {
                    setSprungblattSubmitting(false)
                  }
                }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={sprungblattFormData.name === 'show-form' ? '' : sprungblattFormData.name}
                        onChange={(e) => setSprungblattFormData({ ...sprungblattFormData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Ihr Name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        required
                        value={sprungblattFormData.email}
                        onChange={(e) => setSprungblattFormData({ ...sprungblattFormData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="ihre@email.de"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Firma
                      </label>
                      <input
                        type="text"
                        value={sprungblattFormData.company}
                        onChange={(e) => setSprungblattFormData({ ...sprungblattFormData, company: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Ihre Firma (optional)"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Projektbeschreibung *
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={sprungblattFormData.message}
                        onChange={(e) => setSprungblattFormData({ ...sprungblattFormData, message: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Beschreiben Sie Ihre Idee für eine erweiterte Web-App..."
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setSprungblattFormData({ name: '', email: '', company: '', message: '' })
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Zurück
                      </button>
                      <button
                        type="submit"
                        disabled={sprungblattSubmitting}
                        className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {sprungblattSubmitting ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Sende...
                          </>
                        ) : (
                          'Anfrage senden'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewLink && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPreviewModal(false)
            }
          }}
        >
          <div className="bg-white rounded-lg w-[95vw] max-w-4xl h-[85vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                QR-Code Vorschau: {previewLink.title}
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content - Use BasePreview exactly as in step 2 */}
            <div className="h-[calc(100%-4rem)] bg-gray-50">
              <BasePreview
                title={previewLink.title}
                hideViewControls={false}
                hideZoomControls={false}
              >
                {console.log('🔍 Preview Modal - previewLink:', previewLink)}
                {console.log('🔍 Preview Modal - metadata:', previewLink.metadata)}
                {console.log('🔍 Preview Modal - colorMode:', previewLink.metadata?.colorMode)}
                {console.log('🔍 Preview Modal - customColors:', previewLink.metadata?.customColors)}
                <UnifiedPreview
                  type={previewLink.type as any}
                  data={{
                    // Spread the entire metadata object to get all fields
                    ...(previewLink.metadata || {}),
                    // Ensure critical fields are not overridden by metadata
                    title: previewLink.metadata?.title || previewLink.title,
                    description: previewLink.description || previewLink.metadata?.description,
                    target_url: previewLink.target_url,
                    pdf_url: previewLink.pdf_url,
                    customColors: previewLink.metadata?.customColors,
                    // For sample type, ensure all required fields are passed
                    author: previewLink.metadata?.author,
                    pdfUrl: previewLink.metadata?.pdfUrl || previewLink.pdf_url,
                    coverUrl: previewLink.metadata?.coverUrl,
                    genre: previewLink.metadata?.genre,
                    isbn: previewLink.metadata?.isbn,
                    publisher: previewLink.metadata?.publisher,
                    emailGateEnabled: previewLink.metadata?.emailGateEnabled,
                    downloadEnabled: previewLink.metadata?.downloadEnabled,
                    fonts: previewLink.metadata?.fonts,
                    textSizes: previewLink.metadata?.textSizes,
                    companyPosition: previewLink.metadata?.companyPosition
                  }}
                  brandingType={previewLink.branding_type || 'beyondpages'}
                  customBranding={userBranding}
                  colorMode={previewLink.metadata?.colorMode || 'gradient'}
                />
              </BasePreview>
            </div>
          </div>
        </div>
      )}

      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeModalFeature}
        currentUsage={qrUsageInfo ? {
          used: qrUsageInfo.used,
          limit: qrUsageInfo.limit,
          type: 'QR-Codes'
        } : undefined}
      />
    </div>
  )
}
